import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { courseAPI, chapterAPI, learningAssetAPI, quizAPI, flashcardAPI, interviewAPI, assetQuizAPI, assignmentAPI, BASE_URL } from '../services/api';
import FileUploader from '../components/FileUploader';
import RichTextEditor from '../components/RichTextEditor';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CourseAIQuestions from '../components/CourseAIQuestions';
import TeachingAnalysisModal from '../components/TeachingAnalysisModal';
import Select from 'react-select';
import { Modal, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Helper functions to parse video URLs
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/shorts\/([^&\n?#]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) return match[1];
  }
  return null;
};

const getVimeoVideoId = (url) => {
  if (!url) return null;
  // Match vimeo.com/VIDEO_ID or vimeo.com/VIDEO_ID/HASH
  const match = url.match(/vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/);
  if (match) {
    // Return video ID and optional privacy hash
    return match[2] ? `${match[1]}?h=${match[2]}` : match[1];
  }
  return null;
};

const getVideoEmbedUrl = (url) => {
  if (!url) return null;
  
  const youtubeId = getYouTubeVideoId(url);
  if (youtubeId) {
    return `https://www.youtube.com/embed/${youtubeId}`;
  }
  
  const vimeoId = getVimeoVideoId(url);
  if (vimeoId) {
    // Add parameters for better compatibility and privacy
    // autopause=0: Don't pause when another video plays
    // player_id=0: Unique player identifier
    return `https://player.vimeo.com/video/${vimeoId}${vimeoId.includes('?') ? '&' : '?'}dnt=1&byline=0&portrait=0&title=0&autopause=0`;
  }
  
  return null;
};

// Fetch YouTube video duration
const fetchYouTubeDuration = async (videoId) => {
  try {
    // Check if YouTube API key is available (from environment)
    const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY;
    
    if (!apiKey) {
      console.warn('YouTube API key not configured. Add REACT_APP_YOUTUBE_API_KEY to .env file for YouTube duration detection.');
      return null;
    }
    
    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?id=${videoId}&part=contentDetails&key=${apiKey}`
    );
    
    if (!response.ok) {
      console.error('Failed to fetch YouTube video data');
      return null;
    }
    
    const data = await response.json();
    if (data.items && data.items.length > 0) {
      const duration = data.items[0].contentDetails.duration;
      // Convert ISO 8601 duration (PT1H2M10S) to seconds
      return parseISO8601Duration(duration);
    }
  } catch (error) {
    console.error('Error fetching YouTube duration:', error);
  }
  return null;
};

// Fetch Vimeo video duration
const fetchVimeoDuration = async (videoId) => {
  try {
    // Extract just the numeric ID without the hash parameter
    const numericId = videoId.split('?')[0];
    const response = await fetch(
      `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${numericId}`
    );
    
    if (!response.ok) {
      console.error('Failed to fetch Vimeo video info');
      return null;
    }
    
    const data = await response.json();
    return data.duration || null; // Vimeo oEmbed returns duration in seconds
  } catch (error) {
    console.error('Error fetching Vimeo duration:', error);
  }
  return null;
};

// Parse ISO 8601 duration format (PT1H2M10S) to seconds
const parseISO8601Duration = (duration) => {
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return 0;
  
  const hours = parseInt(match[1] || 0);
  const minutes = parseInt(match[2] || 0);
  const seconds = parseInt(match[3] || 0);
  
  return hours * 3600 + minutes * 60 + seconds;
};

function CourseBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('structure'); // structure, assets, ai-questions
  const [showChapterModal, setShowChapterModal] = useState(false);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [editingChapter, setEditingChapter] = useState(null);
  const [createdChapter, setCreatedChapter] = useState(null); // Track successfully created chapter
  const [viewingAsset, setViewingAsset] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);
  const [createdAsset, setCreatedAsset] = useState(null); // Track successfully created asset
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null); // { assetId, index, question, answer }
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null); // { assetId, index }
  
  // Study questions generation states
  const [showStudyQuestionsModal, setShowStudyQuestionsModal] = useState(false);
  const [studyQuestionsSettings, setStudyQuestionsSettings] = useState({
    numQuestions: 5
  });
  
  // Quiz generation states
  const [showQuizGenerationModal, setShowQuizGenerationModal] = useState(false);
  const [generatingQuiz, setGeneratingQuiz] = useState(false);
  const [assetQuizzes, setAssetQuizzes] = useState({}); // Map of assetId -> quiz data
  const [quizGenerationSettings, setQuizGenerationSettings] = useState({
    numQuestions: 5,
    questionTypes: ['MCQ', 'TrueFalse'],
    difficulties: ['Easy'],
    showAnswerAfterEach: true
  });

  // Transcript title editing states
  const [editingTranscriptTitle, setEditingTranscriptTitle] = useState(false);
  const [transcriptTitleValue, setTranscriptTitleValue] = useState('');
  const [editingQuizQuestion, setEditingQuizQuestion] = useState(null); // For editing quiz questions
  const [showQuizQuestionModal, setShowQuizQuestionModal] = useState(false);
  const [quizQuestionFormData, setQuizQuestionFormData] = useState({
    question: '',
    questionType: 'MCQ',
    options: ['', '', '', ''],
    correctAnswer: 0,
    explanation: '',
    difficulty: 'Medium'
  });
  
  // Assignment generation states
  const [showAssignmentGenerationModal, setShowAssignmentGenerationModal] = useState(false);
  const [generatingAssignments, setGeneratingAssignments] = useState(false);
  const [generatedAssignments, setGeneratedAssignments] = useState([]);
  const [assignmentGenerationSettings, setAssignmentGenerationSettings] = useState({
    language: 'javascript',
    typeCounts: {
      concept_reinforcement: 0,
      debugging: 0,
      small_coding: 1,
      feature_implementation: 0,
      output_prediction: 0,
      mini_project: 0
    }
  });
  const [selectedAssignments, setSelectedAssignments] = useState([]); // IDs of assignments to save
  const [savedAssignmentsForAsset, setSavedAssignmentsForAsset] = useState([]); // Assignments already saved for current asset
  const [loadingSavedAssignments, setLoadingSavedAssignments] = useState(false);
  
  // Problem statement generation states
  const [showProblemStatementModal, setShowProblemStatementModal] = useState(false);
  const [selectedAssignmentForStatement, setSelectedAssignmentForStatement] = useState(null);
  const [problemStatementInput, setProblemStatementInput] = useState('');
  const [generatedProblemStatement, setGeneratedProblemStatement] = useState('');
  const [generatingStatement, setGeneratingStatement] = useState(false);
  const [statementLength, setStatementLength] = useState(''); // 'short', 'medium', or 'full'
  
  // Lecture Assignments states
  const [lectureAssignments, setLectureAssignments] = useState([]);
  const [loadingLectureAssignments, setLoadingLectureAssignments] = useState(false);
  const [lectureAssignmentFilters, setLectureAssignmentFilters] = useState({
    type: '',
    complexity: '',
    language: '',
    search: ''
  });
  const [viewingLectureAssignment, setViewingLectureAssignment] = useState(null);
  
  // Teaching analysis states
  const [showTeachingAnalysis, setShowTeachingAnalysis] = useState(false);
  const [teachingAnalysisAsset, setTeachingAnalysisAsset] = useState(null);
  
  const [availableQuizzes, setAvailableQuizzes] = useState([]);
  const [availableFlashcards, setAvailableFlashcards] = useState([]);
  const [availableInterviews, setAvailableInterviews] = useState([]);
  const [assetTypeFilter, setAssetTypeFilter] = useState('all'); // Filter for asset types
  
  // Import Asset feature
  const [assetModalTab, setAssetModalTab] = useState('add'); // 'add' or 'import'
  const [importAssets, setImportAssets] = useState([]);
  const [importSearchTerm, setImportSearchTerm] = useState('');
  const [importTypeFilter, setImportTypeFilter] = useState('');
  const [importCourseFilter, setImportCourseFilter] = useState('');
  const [importChapterFilter, setImportChapterFilter] = useState('');
  const [importCourseOptions, setImportCourseOptions] = useState([]);
  const [importChapterOptions, setImportChapterOptions] = useState([]);
  const [loadingImportCourses, setLoadingImportCourses] = useState(false);
  const [loadingImportChapters, setLoadingImportChapters] = useState(false);
  const [loadingImportAssets, setLoadingImportAssets] = useState(false);
  const [showImportModeModal, setShowImportModeModal] = useState(false);
  const [selectedAssetsToImport, setSelectedAssetsToImport] = useState([]);
  
  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalChapters, setOriginalChapters] = useState([]);
  const [originalAssets, setOriginalAssets] = useState([]);
  const [pendingOperations, setPendingOperations] = useState([]);
  
  // Chapter sorting preference
  const [chapterSortOrder, setChapterSortOrder] = useState(() => {
    return localStorage.getItem('courseBuilder_chapterSortOrder') || 'old-new';
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const [chapterForm, setChapterForm] = useState({
    title: '',
    description: '',
    content: '',
    parentChapter: null,
    order: 0
  });

  const [assetForm, setAssetForm] = useState({
    title: '',
    description: '',
    assetType: 'note',
    chapter: null,
    content: '',
    externalUrl: '',
    fileUrl: '',
    fileType: '',
    fileName: '',
    fileSize: 0,
    filePublicId: '',
    order: 0,
    isRequired: false,
    estimatedDuration: null, // Duration in minutes
    contentReference: {
      refModel: null,
      refId: null
    }
  });
  
  const [videoSourceType, setVideoSourceType] = useState('upload'); // 'upload' or 'url'
  const [processingTranscript, setProcessingTranscript] = useState(false);
  const [fetchingVideoDuration, setFetchingVideoDuration] = useState(false);

  // Handle chapter sort order change
  const handleChapterSortChange = (newSortOrder) => {
    setChapterSortOrder(newSortOrder);
    localStorage.setItem('courseBuilder_chapterSortOrder', newSortOrder);
  };

  // Get sorted chapters based on current sort preference
  const getSortedChapters = (chaptersToSort) => {
    const sortedChapters = [...chaptersToSort];
    
    if (chapterSortOrder === 'new-old') {
      // Sort by createdAt descending (newest first)
      sortedChapters.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });
    } else {
      // Sort by createdAt ascending (oldest first) - default is 'old-new'
      sortedChapters.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateA - dateB;
      });
    }
    
    return sortedChapters;
  };

  const fetchCourseDetails = async () => {
    try {
      setLoading(true);
      const response = await courseAPI.getCourse(id);
      const courseData = response.data;
      setCourse(courseData.course);
      setChapters(courseData.chapters || []);
      setOriginalChapters(JSON.parse(JSON.stringify(courseData.chapters || [])));
      
      const assetData = courseData.assets || [];
      console.log('Fetched assets:', assetData.length, 'assets');
      console.log('Transcript assets:', assetData.filter(a => a.assetType === 'transcript').length);
      
      // Debug transcript assets
      const transcripts = assetData.filter(a => a.assetType === 'transcript');
      transcripts.forEach(t => {
        console.log('Transcript:', t.title, 'Chapter:', t.chapter);
      });
      
      setAssets(assetData);
      setOriginalAssets(JSON.parse(JSON.stringify(assetData)));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role !== 'admin') {
      setError('Access denied. Admin only.');
      navigate('/courses');
    } else if (id) {
      fetchCourseDetails();
      fetchAvailableQuizzes();
      fetchAvailableFlashcards();
      fetchAvailableInterviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user, navigate]);

  // Warn user before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Load quiz data when viewing an asset
  useEffect(() => {
    const loadAssetQuiz = async () => {
      if (viewingAsset && (viewingAsset.assetType === 'note' || viewingAsset.assetType === 'markdown' || viewingAsset.assetType === 'transcript')) {
        try {
          const response = await assetQuizAPI.getAssetQuiz(viewingAsset._id);
          if (response.data.assetQuiz) {
            setAssetQuizzes(prev => ({
              ...prev,
              [viewingAsset._id]: response.data.assetQuiz
            }));
          }
        } catch (err) {
          // No quiz exists yet, which is fine
        }
      }
    };
    
    loadAssetQuiz();
  }, [viewingAsset]);

  // Load all asset quizzes when assets tab is viewed
  useEffect(() => {
    if ((activeTab === 'assets' || activeTab === 'structure') && assets.length > 0) {
      fetchAssetQuizzes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, assets.length]);

  // Load lecture assignments when the lecture-assignments tab is active
  useEffect(() => {
    if (activeTab === 'lecture-assignments') {
      fetchLectureAssignments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, lectureAssignmentFilters]);

  // Load course list when import tab is opened
  useEffect(() => {
    if (showAssetModal && assetModalTab === 'import' && importCourseOptions.length === 0) {
      fetchImportCourses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAssetModal, assetModalTab]);

  // Load chapters when a source course is selected for import filtering
  useEffect(() => {
    if (importCourseFilter) {
      fetchImportChapters(importCourseFilter);
    } else {
      setImportChapterOptions([]);
      setImportChapterFilter('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importCourseFilter]);

  const fetchAvailableQuizzes = async () => {
    try {
      const response = await quizAPI.getQuizList();
      setAvailableQuizzes(response.data || []);
    } catch (err) {
      console.error('Error fetching quizzes:', err);
    }
  };

  const fetchAvailableFlashcards = async () => {
    try {
      const response = await flashcardAPI.getAllFlashcards();
      setAvailableFlashcards(response.data || []);
    } catch (err) {
      console.error('Error fetching flashcards:', err);
    }
  };

  const fetchAvailableInterviews = async () => {
    try {
      const response = await interviewAPI.getInterviews(1, 1000); // Get first 1000 interviews
      setAvailableInterviews(response.data.interviews || []);
    } catch (err) {
      console.error('Error fetching interviews:', err);
    }
  };

  const fetchLectureAssignments = async () => {
    setLoadingLectureAssignments(true);
    try {
      console.log('Fetching lecture assignments with filters:', lectureAssignmentFilters);
      const response = await assignmentAPI.getAllLectureAssignments({
        type: lectureAssignmentFilters.type || undefined,
        complexity: lectureAssignmentFilters.complexity || undefined,
        language: lectureAssignmentFilters.language || undefined,
        page: 1,
        limit: 100
      });
      console.log('Lecture assignments response:', response.data);
      setLectureAssignments(response.data.assignments || []);
    } catch (err) {
      console.error('Error fetching lecture assignments:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      alert(`Failed to load lecture assignments: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoadingLectureAssignments(false);
    }
  };

  const fetchAssetQuizzes = async () => {
    // Load quiz data for assets that can have generated quizzes
    const eligibleAssets = assets.filter(
      asset =>
        asset.assetType === 'note' ||
        asset.assetType === 'markdown' ||
        asset.assetType === 'transcript'
    );
    
    const quizPromises = eligibleAssets.map(async (asset) => {
      try {
        const response = await assetQuizAPI.getAssetQuiz(asset._id);
        return { assetId: asset._id, quiz: response.data.assetQuiz };
      } catch (err) {
        return { assetId: asset._id, quiz: null };
      }
    });

    const results = await Promise.all(quizPromises);
    const quizMap = {};
    results.forEach(({ assetId, quiz }) => {
      if (quiz) {
        quizMap[assetId] = quiz;
      }
    });
    
    setAssetQuizzes(prev => ({ ...prev, ...quizMap }));
  };

  const handleCreateChapter = async (e) => {
    e.preventDefault();
    try {
      if (editingChapter) {
        // Update existing chapter in local state
        const updatedChapters = chapters.map(ch => 
          ch._id === editingChapter._id ? { ...ch, ...chapterForm } : ch
        );
        setChapters(updatedChapters);
        
        // Track the update operation
        setPendingOperations(prev => [...prev, {
          type: 'updateChapter',
          id: editingChapter._id,
          data: chapterForm
        }]);
        
        // Set the updated chapter as created to show action buttons
        const updatedChapter = updatedChapters.find(ch => ch._id === editingChapter._id);
        setCreatedChapter(updatedChapter);
      } else {
        // Create new chapter in local state with temporary ID
        const tempId = 'temp_' + Date.now();
        const currentDate = new Date().toISOString();
        
        // Calculate the correct order - should be at the end of all chapters
        const maxOrder = chapters.length > 0 
          ? Math.max(...chapters.map(ch => ch.order || 0)) 
          : -1;
        const newOrder = maxOrder + 1;
        
        const newChapter = {
          _id: tempId,
          ...chapterForm,
          order: newOrder,
          course: id,
          parentChapter: selectedChapter,
          isTemp: true,
          createdAt: currentDate
        };
        setChapters([...chapters, newChapter]);
        
        // Track the create operation
        setPendingOperations(prev => [...prev, {
          type: 'createChapter',
          tempId: tempId,
          data: {
            ...chapterForm,
            order: newOrder,
            course: id,
            parentChapter: selectedChapter
          }
        }]);
        
        // Set the created chapter to show action buttons
        setCreatedChapter(newChapter);
      }
      
      setHasUnsavedChanges(true);
      // Don't close the modal - keep it open to show action buttons
      // setShowChapterModal(false);
      setChapterForm({ title: '', description: '', content: '', parentChapter: null, order: 0 });
      // Don't reset selectedChapter and editingChapter yet
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${editingChapter ? 'update' : 'create'} chapter`);
    }
  };

  // Chapter modal action handlers
  const handleChapterModalEdit = () => {
    if (createdChapter) {
      setEditingChapter(createdChapter);
      setChapterForm({
        title: createdChapter.title || '',
        description: createdChapter.description || '',
        content: createdChapter.content || '',
        parentChapter: createdChapter.parentChapter || null,
        order: createdChapter.order || 0
      });
      setCreatedChapter(null);
    }
  };

  const handleChapterModalAddSubChapter = () => {
    if (createdChapter) {
      setSelectedChapter(createdChapter._id);
      setEditingChapter(null);
      setChapterForm({ title: '', description: '', content: '', parentChapter: createdChapter._id, order: 0 });
      setCreatedChapter(null);
    }
  };

  const handleChapterModalAddAsset = () => {
    if (createdChapter) {
      setShowChapterModal(false);
      setCreatedChapter(null);
      setEditingChapter(null);
      setSelectedChapter(createdChapter._id);
      setChapterForm({ title: '', description: '', content: '', parentChapter: null, order: 0 });
      
      // Open asset modal with the chapter pre-selected
      setAssetForm({ 
        ...assetForm, 
        chapter: createdChapter._id,
        order: assets.filter(a => a.chapter === createdChapter._id).length
      });
      setShowAssetModal(true);
    }
  };

  const handleChapterModalDelete = () => {
    if (createdChapter) {
      handleDeleteChapter(createdChapter._id);
      setShowChapterModal(false);
      setCreatedChapter(null);
      setEditingChapter(null);
      setSelectedChapter(null);
      setChapterForm({ title: '', description: '', content: '', parentChapter: null, order: 0 });
    }
  };

  const handleChapterModalClose = () => {
    setShowChapterModal(false);
    setCreatedChapter(null);
    setEditingChapter(null);
    setSelectedChapter(null);
    setChapterForm({ title: '', description: '', content: '', parentChapter: null, order: 0 });
  };

  // Asset modal action handlers
  const handleAssetModalEdit = () => {
    if (createdAsset) {
      setEditingAsset(createdAsset);
      setAssetForm({
        title: createdAsset.title || '',
        description: createdAsset.description || '',
        assetType: createdAsset.assetType || 'note',
        chapter: createdAsset.chapter || null,
        content: createdAsset.content || '',
        externalUrl: createdAsset.externalUrl || '',
        fileUrl: createdAsset.fileUrl || '',
        fileType: createdAsset.fileType || '',
        fileName: createdAsset.fileName || '',
        fileSize: createdAsset.fileSize || 0,
        filePublicId: createdAsset.filePublicId || '',
        order: createdAsset.order || 0,
        isRequired: createdAsset.isRequired || false,
        estimatedDuration: createdAsset.estimatedDuration || null,
        contentReference: createdAsset.contentReference || { refModel: null, refId: null }
      });
      setCreatedAsset(null);
    }
  };

  const handleAssetModalAddAnother = () => {
    if (createdAsset) {
      // Keep the same chapter selected
      const chapterId = createdAsset.chapter;
      setSelectedChapter(chapterId);
      setEditingAsset(null);
      setAssetForm({ 
        title: '', 
        description: '', 
        assetType: 'note', 
        chapter: chapterId, 
        content: '', 
        externalUrl: '', 
        fileUrl: '', 
        fileType: '', 
        fileName: '', 
        fileSize: 0, 
        filePublicId: '', 
        order: 0, 
        isRequired: false, 
        estimatedDuration: null, 
        contentReference: { refModel: null, refId: null } 
      });
      setCreatedAsset(null);
      setAssetModalTab('add');
    }
  };

  const handleAssetModalDelete = () => {
    if (createdAsset) {
      handleDeleteAsset(createdAsset._id);
      setShowAssetModal(false);
      setCreatedAsset(null);
      setEditingAsset(null);
      setSelectedChapter(null);
      setAssetForm({ title: '', description: '', assetType: 'note', chapter: null, content: '', externalUrl: '', fileUrl: '', fileType: '', fileName: '', fileSize: 0, filePublicId: '', order: 0, isRequired: false, estimatedDuration: null, contentReference: { refModel: null, refId: null } });
      setVideoSourceType('upload');
    }
  };

  const handleAssetModalClose = () => {
    setShowAssetModal(false);
    setCreatedAsset(null);
    setEditingAsset(null);
    setSelectedChapter(null);
    setVideoSourceType('upload');
    setAssetForm({ title: '', description: '', assetType: 'note', chapter: null, content: '', externalUrl: '', fileUrl: '', fileType: '', fileName: '', fileSize: 0, filePublicId: '', order: 0, isRequired: false, estimatedDuration: null, contentReference: { refModel: null, refId: null } });
    setAssetModalTab('add');
    setImportSearchTerm('');
    setImportTypeFilter('');
    setImportCourseFilter('');
    setImportChapterFilter('');
    setImportCourseOptions([]);
    setImportChapterOptions([]);
    setImportAssets([]);
    setSelectedAssetsToImport([]);
    setShowImportModeModal(false);
  };

  // Handle transcript title edit
  const handleSaveTranscriptTitle = async () => {
    if (!transcriptTitleValue.trim()) {
      alert('Title cannot be empty');
      return;
    }

    try {
      // Update the asset in local state
      const updatedAssets = assets.map(asset => 
        asset._id === viewingAsset._id 
          ? { ...asset, title: transcriptTitleValue }
          : asset
      );
      setAssets(updatedAssets);

      // Find and update viewingAsset from the updated assets array
      const updatedViewingAsset = updatedAssets.find(asset => asset._id === viewingAsset._id);
      if (updatedViewingAsset) {
        setViewingAsset(updatedViewingAsset);
      }

      // Extract chapter ID for the update - preserve existing chapter reference
      const chapterId = viewingAsset.chapter?._id || viewingAsset.chapter;
      
      console.log('Saving transcript title:', {
        assetId: viewingAsset._id,
        oldTitle: viewingAsset.title,
        newTitle: transcriptTitleValue,
        chapterId: chapterId,
        chapterObject: viewingAsset.chapter
      });

      // Track the update operation - include chapter to preserve the reference
      setPendingOperations(prev => [...prev, {
        type: 'updateAsset',
        id: viewingAsset._id,
        data: { 
          title: transcriptTitleValue,
          chapter: chapterId // Preserve chapter reference
        }
      }]);

      setHasUnsavedChanges(true);
      setEditingTranscriptTitle(false);
    } catch (err) {
      console.error('Error updating transcript title:', err);
      alert('Failed to update title');
    }
  };

  const handleCreateAsset = async (e) => {
    e.preventDefault();
    try {
      if (editingAsset) {
        // Update existing asset in local state
        const updatedAssets = assets.map(asset => 
          asset._id === editingAsset._id ? { ...asset, ...assetForm } : asset
        );
        setAssets(updatedAssets);
        
        // Track the update operation
        setPendingOperations(prev => [...prev, {
          type: 'updateAsset',
          id: editingAsset._id,
          data: assetForm
        }]);
        
        // Set the updated asset as created to show action buttons
        const updatedAsset = updatedAssets.find(a => a._id === editingAsset._id);
        setCreatedAsset(updatedAsset);
      } else {
        // Create new asset in local state with temporary ID
        const tempId = 'temp_' + Date.now();
        
        // Calculate the correct order - should be at the end of the chapter's assets
        // Normalize chapter IDs for comparison (handles populated objects vs IDs)
        const chapterAssets = assets.filter(a => {
          const aChapterId = a.chapter?._id || a.chapter;
          return aChapterId === selectedChapter || (aChapterId === null && selectedChapter === null);
        });
        const maxOrder = chapterAssets.length > 0 
          ? Math.max(...chapterAssets.map(a => a.order || 0)) 
          : -1;
        const newOrder = maxOrder + 1;
        
        const newAsset = {
          _id: tempId,
          ...assetForm,
          order: newOrder,
          course: id,
          chapter: selectedChapter,
          isTemp: true
        };
        setAssets([...assets, newAsset]);
        
        // Track the create operation
        setPendingOperations(prev => [...prev, {
          type: 'createAsset',
          tempId: tempId,
          data: {
            ...assetForm,
            order: newOrder,
            course: id,
            chapter: selectedChapter
          }
        }]);
        
        // Set the created asset to show action buttons
        setCreatedAsset(newAsset);
      }
      
      setHasUnsavedChanges(true);
      // Don't close modal - keep it open to show action buttons
      // setShowAssetModal(false);
      setAssetForm({ title: '', description: '', assetType: 'note', chapter: null, content: '', externalUrl: '', fileUrl: '', fileType: '', fileName: '', fileSize: 0, filePublicId: '', order: 0, isRequired: false, estimatedDuration: null, contentReference: { refModel: null, refId: null } });
      setVideoSourceType('upload');
      // Don't reset selectedChapter and editingAsset yet
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${editingAsset ? 'update' : 'create'} asset`);
    }
  };



  const handleFileUpload = (fileData) => {
    const updates = {
      fileUrl: fileData.url,
      fileType: fileData.type,
      fileName: fileData.name,
      fileSize: fileData.size
    };
    
    // If video duration is available, convert seconds to minutes and store
    if (fileData.duration) {
      updates.estimatedDuration = Math.ceil(fileData.duration / 60);
    }
    
    setAssetForm(prev => ({
      ...prev,
      ...updates
    }));
    setShowFileUploadModal(false);
  };

  const handleExternalUrlChange = async (url) => {
    setAssetForm(prev => ({ ...prev, externalUrl: url }));
    
    // If it's a video asset and the URL is a YouTube or Vimeo link, fetch duration
    if (assetForm.assetType === 'video' && url) {
      const youtubeId = getYouTubeVideoId(url);
      const vimeoId = getVimeoVideoId(url);
      
      if (youtubeId || vimeoId) {
        setFetchingVideoDuration(true);
        try {
          let duration = null;
          
          if (youtubeId) {
            duration = await fetchYouTubeDuration(youtubeId);
            if (!duration) {
              console.log('YouTube duration not available. Configure REACT_APP_YOUTUBE_API_KEY for automatic duration detection.');
            }
          } else if (vimeoId) {
            duration = await fetchVimeoDuration(vimeoId);
          }
          
          if (duration) {
            // Convert seconds to minutes
            const durationInMinutes = Math.ceil(duration / 60);
            setAssetForm(prev => ({
              ...prev,
              estimatedDuration: durationInMinutes
            }));
          } else {
            // Clear duration if we couldn't fetch it
            setAssetForm(prev => ({
              ...prev,
              estimatedDuration: null
            }));
          }
        } catch (error) {
          console.error('Error fetching video duration:', error);
        } finally {
          setFetchingVideoDuration(false);
        }
      } else {
        // Not a valid YouTube/Vimeo URL, clear duration
        setAssetForm(prev => ({
          ...prev,
          estimatedDuration: null
        }));
      }
    }
  };

  const handleTranscriptUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    const allowedExtensions = ['.txt', '.vtt', '.srt'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      setError('Invalid file type. Please upload a .txt, .vtt, or .srt file.');
      return;
    }

    setProcessingTranscript(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', assetForm.title || file.name);

      const response = await fetch(`${BASE_URL}/api/learning-asset/process-transcript`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to process transcript');
      }

      const data = await response.json();

      // Update asset form with processed content
      setAssetForm(prev => ({
        ...prev,
        content: data.processedContent,
        fileUrl: data.file.url,
        fileType: data.file.type,
        fileName: data.file.name,
        fileSize: data.file.size,
        filePublicId: data.file.publicId
      }));

      setError('');
    } catch (err) {
      setError(err.message || 'Failed to process transcript');
    } finally {
      setProcessingTranscript(false);
    }
  };

  const handleMarkdownPDFUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError('Invalid file type. Please upload a PDF file.');
      return;
    }

    setProcessingTranscript(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', assetForm.title || file.name);

      const response = await fetch(`${BASE_URL}/api/learning-asset/process-pdf-markdown`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to process PDF');
      }

      const data = await response.json();

      // Update asset form with processed markdown content
      setAssetForm(prev => ({
        ...prev,
        content: data.markdownContent,
        fileUrl: data.file.url,
        fileType: data.file.type,
        fileName: data.file.name,
        fileSize: data.file.size,
        filePublicId: data.file.publicId
      }));

      setError('');
    } catch (err) {
      setError(err.message || 'Failed to process PDF');
    } finally {
      setProcessingTranscript(false);
    }
  };

  const fetchImportCourses = async () => {
    try {
      setLoadingImportCourses(true);
      const response = await courseAPI.getCourseList(1, 500);
      const courses = response.data?.courses || response.data || [];
      setImportCourseOptions(Array.isArray(courses) ? courses : []);
    } catch (err) {
      console.error('Error fetching courses for import filter:', err);
      setError(err.response?.data?.message || 'Failed to load courses');
    } finally {
      setLoadingImportCourses(false);
    }
  };

  const flattenChaptersForImport = (chapterList, level = 0) => {
    const flattened = [];
    (chapterList || []).forEach(chapter => {
      flattened.push({
        ...chapter,
        title: `${'  '.repeat(level)}${chapter.title}`
      });
      if (chapter.subChapters?.length) {
        flattened.push(...flattenChaptersForImport(chapter.subChapters, level + 1));
      }
    });
    return flattened;
  };

  const fetchImportChapters = async (courseId) => {
    try {
      setLoadingImportChapters(true);
      const response = await chapterAPI.getCourseChapters(courseId);
      const chaptersList = response.data?.chapters || response.data || [];
      setImportChapterOptions(
        Array.isArray(chaptersList) ? flattenChaptersForImport(chaptersList) : []
      );
    } catch (err) {
      console.error('Error fetching chapters for import filter:', err);
      setImportChapterOptions([]);
    } finally {
      setLoadingImportChapters(false);
    }
  };

  // Fetch assets for import
  const fetchImportAssets = async () => {
    try {
      setLoadingImportAssets(true);
      const params = new URLSearchParams({
        page: '1',
        limit: '100'
      });
      
      if (importSearchTerm) params.append('search', importSearchTerm);
      if (importTypeFilter) params.append('assetType', importTypeFilter);
      if (importCourseFilter) params.append('courseId', importCourseFilter);
      if (importChapterFilter) params.append('chapterId', importChapterFilter);

      const response = await learningAssetAPI.getAllAssets(params.toString());
      let availableAssets = response.data.assets || [];

      // When no source course is selected, hide assets from the course being edited
      if (!importCourseFilter) {
        const currentCourseId = String(id);
        availableAssets = availableAssets.filter(asset => {
          const assetCourseId = String(asset.course?._id || asset.course);
          return assetCourseId !== currentCourseId;
        });
      }

      // Show filtered import results by chapter asset order (ascending).
      // For broader filters, keep ordering stable by course/chapter then asset order.
      availableAssets.sort((a, b) => {
        const courseA = String(a.course?._id || a.course || '');
        const courseB = String(b.course?._id || b.course || '');
        const chapterA = String(a.chapter?._id || a.chapter || '');
        const chapterB = String(b.chapter?._id || b.chapter || '');
        const orderA = Number.isFinite(a.order) ? a.order : Number.MAX_SAFE_INTEGER;
        const orderB = Number.isFinite(b.order) ? b.order : Number.MAX_SAFE_INTEGER;

        if (importChapterFilter) {
          return orderA - orderB;
        }

        if (courseA !== courseB) return courseA.localeCompare(courseB);
        if (chapterA !== chapterB) return chapterA.localeCompare(chapterB);
        return orderA - orderB;
      });
      
      setImportAssets(availableAssets);
      setSelectedAssetsToImport(prevSelected =>
        prevSelected.filter(selected =>
          availableAssets.some(asset => asset._id === selected._id)
        )
      );
    } catch (err) {
      console.error('Error fetching import assets:', err);
      setError(err.response?.data?.message || 'Failed to fetch assets');
    } finally {
      setLoadingImportAssets(false);
    }
  };

  const handleToggleAssetImportSelection = (asset) => {
    setSelectedAssetsToImport(prevSelected => {
      const isAlreadySelected = prevSelected.some(selected => selected._id === asset._id);
      if (isAlreadySelected) {
        return prevSelected.filter(selected => selected._id !== asset._id);
      }
      return [...prevSelected, asset];
    });
  };

  const handleToggleSelectAllImportAssets = (shouldSelectAll) => {
    if (shouldSelectAll) {
      setSelectedAssetsToImport(importAssets);
      return;
    }
    setSelectedAssetsToImport([]);
  };

  const isTranscriptFromOtherCourse = (asset) =>
    asset.assetType === 'transcript' &&
    String(asset.course?._id || asset.course) !== String(id);

  const getTranscriptImportReuseFields = (sourceAsset) => {
    if (!isTranscriptFromOtherCourse(sourceAsset)) return {};
    return {
      sourceAssetId: sourceAsset._id,
      ...(sourceAsset.studyQuestions?.length ? { studyQuestions: sourceAsset.studyQuestions } : {}),
      ...(sourceAsset.teachingAnalysis ? { teachingAnalysis: sourceAsset.teachingAnalysis } : {})
    };
  };

  const refreshImportedAssetQuizzes = async (importedAssets) => {
    const transcriptAssets = importedAssets.filter(asset => asset.assetType === 'transcript');
    if (transcriptAssets.length === 0) return;

    const quizEntries = await Promise.all(
      transcriptAssets.map(async (asset) => {
        try {
          const response = await assetQuizAPI.getAssetQuiz(asset._id);
          return { assetId: asset._id, quiz: response.data.assetQuiz };
        } catch (err) {
          return { assetId: asset._id, quiz: null };
        }
      })
    );

    setAssetQuizzes(prev => {
      const updated = { ...prev };
      quizEntries.forEach(({ assetId, quiz }) => {
        if (quiz) updated[assetId] = quiz;
      });
      return updated;
    });
  };

  // Handle import assets with link or copy mode
  const handleImportAssets = async (asLink) => {
    try {
      if (selectedAssetsToImport.length === 0) return;

      // Calculate the correct order
      const chapterAssets = assets.filter(a => {
        const aChapterId = a.chapter?._id || a.chapter;
        return aChapterId === selectedChapter || (aChapterId === null && selectedChapter === null);
      });
      const maxOrder = chapterAssets.length > 0 
        ? Math.max(...chapterAssets.map(a => a.order || 0)) 
        : -1;
      const startingOrder = maxOrder + 1;
      const isTempChapter = typeof selectedChapter === 'string' && selectedChapter.startsWith('temp_');

      // If target chapter is unsaved, keep imported assets staged until course changes are saved.
      if (isTempChapter) {
        const newAssetsToAdd = [];
        const operationsToAdd = [];

        selectedAssetsToImport.forEach((asset, index) => {
          const tempId = `temp_${Date.now()}_${index}`;
          const nextOrder = startingOrder + index;
          const reuseFields = getTranscriptImportReuseFields(asset);

          if (asLink) {
            newAssetsToAdd.push({
              _id: tempId,
              title: asset.title,
              description: asset.description,
              assetType: asset.assetType,
              course: id,
              chapter: selectedChapter,
              order: nextOrder,
              isRequired: false,
              linkedAsset: asset._id,
              isTemp: true,
              content: asset.content,
              externalUrl: asset.externalUrl,
              fileUrl: asset.fileUrl,
              fileType: asset.fileType,
              fileName: asset.fileName,
              fileSize: asset.fileSize,
              filePublicId: asset.filePublicId,
              contentReference: asset.contentReference,
              studyQuestions: asset.studyQuestions || [],
              teachingAnalysis: asset.teachingAnalysis,
              ...reuseFields
            });
          } else {
            newAssetsToAdd.push({
              _id: tempId,
              title: `${asset.title} (Copy)`,
              description: asset.description,
              assetType: asset.assetType,
              course: id,
              chapter: selectedChapter,
              order: nextOrder,
              isRequired: false,
              isTemp: true,
              content: asset.content,
              externalUrl: asset.externalUrl,
              fileUrl: asset.fileUrl,
              fileType: asset.fileType,
              fileName: asset.fileName,
              fileSize: asset.fileSize,
              filePublicId: asset.filePublicId,
              contentReference: asset.contentReference,
              studyQuestions: asset.studyQuestions || [],
              teachingAnalysis: asset.teachingAnalysis,
              ...reuseFields
            });
          }

          operationsToAdd.push({
            type: 'createAsset',
            tempId: tempId,
            data: {
              title: asLink ? asset.title : `${asset.title} (Copy)`,
              description: asset.description,
              assetType: asset.assetType,
              course: id,
              chapter: selectedChapter,
              order: nextOrder,
              isRequired: false,
              ...(asLink ? { linkedAsset: asset._id } : {}),
              content: asset.content,
              externalUrl: asset.externalUrl,
              fileUrl: asset.fileUrl,
              fileType: asset.fileType,
              fileName: asset.fileName,
              fileSize: asset.fileSize,
              filePublicId: asset.filePublicId,
              contentReference: asset.contentReference,
              ...reuseFields
            }
          });
        });

        setAssets(prev => [...prev, ...newAssetsToAdd]);
        setPendingOperations(prev => [...prev, ...operationsToAdd]);
        setHasUnsavedChanges(true);
      } else {
        const payloads = selectedAssetsToImport.map((asset, index) => {
          const nextOrder = startingOrder + index;
          return {
            title: asLink ? asset.title : `${asset.title} (Copy)`,
            description: asset.description,
            assetType: asset.assetType,
            course: id,
            chapter: selectedChapter,
            order: nextOrder,
            isRequired: false,
            ...(asLink ? { linkedAsset: asset._id } : {}),
            content: asset.content,
            externalUrl: asset.externalUrl,
            fileUrl: asset.fileUrl,
            fileType: asset.fileType,
            fileName: asset.fileName,
            fileSize: asset.fileSize,
            filePublicId: asset.filePublicId,
            contentReference: asset.contentReference,
            ...getTranscriptImportReuseFields(asset)
          };
        });

        const createResponses = await Promise.all(
          payloads.map(payload => learningAssetAPI.createAsset(payload))
        );
        const createdAssets = createResponses
          .map(response => response.data?.asset)
          .filter(Boolean);

        if (createdAssets.length > 0) {
          setAssets(prev => [...prev, ...createdAssets]);
          await refreshImportedAssetQuizzes(createdAssets);
        }
      }

      setShowImportModeModal(false);
      setSelectedAssetsToImport([]);
      setAssetModalTab('import');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to import asset');
    }
  };

  const handleDeleteChapter = async (chapterId) => {
    if (!window.confirm('Delete this chapter and all its sub-chapters and assets?')) return;
    try {
      // Remove from local state
      const updatedChapters = chapters.filter(ch => ch._id !== chapterId);
      setChapters(updatedChapters);
      
      // Track the delete operation (only if not a temporary chapter)
      if (!chapterId.startsWith('temp_')) {
        setPendingOperations(prev => [...prev, {
          type: 'deleteChapter',
          id: chapterId
        }]);
      }
      
      setHasUnsavedChanges(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete chapter');
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm('Delete this asset?')) return;
    try {
      // Remove from local state
      const updatedAssets = assets.filter(asset => asset._id !== assetId);
      setAssets(updatedAssets);
      
      // Track the delete operation (only if not a temporary asset)
      if (!assetId.startsWith('temp_')) {
        setPendingOperations(prev => [...prev, {
          type: 'deleteAsset',
          id: assetId
        }]);
      }
      
      setHasUnsavedChanges(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete asset');
    }
  };

  const handleGenerateStudyQuestions = async (assetId) => {
    // Check if asset has been saved
    const asset = assets.find(a => a._id === assetId);
    if (asset && asset._id.startsWith('temp_')) {
      alert('Please save the course changes first before generating study questions for this asset.');
      return;
    }
    
    // Open modal to select number of questions
    setViewingAsset(asset);
    setShowStudyQuestionsModal(true);
  };
  
  const handleConfirmGenerateStudyQuestions = async () => {
    if (!viewingAsset) return;
    
    try {
      setGeneratingQuestions(true);
      setShowStudyQuestionsModal(false);
      
      const response = await learningAssetAPI.generateStudyQuestions(
        viewingAsset._id,
        studyQuestionsSettings.numQuestions
      );
      
      // Update the asset in local state
      setAssets(prev => prev.map(asset => 
        asset._id === viewingAsset._id ? response.data.asset : asset
      ));
      
      // Update viewingAsset if it's currently being viewed
      if (viewingAsset && viewingAsset._id === viewingAsset._id) {
        setViewingAsset(response.data.asset);
      }
      
      alert(`Successfully generated ${response.data.questionsCount} study questions!`);
      // Reset settings for next time
      setStudyQuestionsSettings({ numQuestions: 5 });
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to generate study questions';
      alert(errorMsg);
      console.error('Error generating study questions:', err);
    } finally {
      setGeneratingQuestions(false);
    }
  };

  const handleEditQuestion = (assetId, index, question, answer) => {
    setEditingQuestion({ assetId, index, question, answer });
    setShowQuestionModal(true);
  };

  const handleSaveQuestion = async () => {
    if (!editingQuestion) return;

    try {
      const response = await learningAssetAPI.updateStudyQuestion(
        editingQuestion.assetId,
        editingQuestion.index,
        {
          question: editingQuestion.question,
          answer: editingQuestion.answer
        }
      );

      // Update local state
      setAssets(prev => prev.map(asset => 
        asset._id === editingQuestion.assetId ? response.data.asset : asset
      ));

      // Update viewingAsset if it's currently being viewed
      if (viewingAsset && viewingAsset._id === editingQuestion.assetId) {
        setViewingAsset(response.data.asset);
      }

      setShowQuestionModal(false);
      setEditingQuestion(null);
      alert('Study question updated successfully!');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update study question');
      console.error('Error updating study question:', err);
    }
  };

  const handleDeleteQuestion = async (assetId, index) => {
    if (!window.confirm('Are you sure you want to delete this study question?')) {
      return;
    }

    try {
      const response = await learningAssetAPI.deleteStudyQuestion(assetId, index);

      // Update local state
      setAssets(prev => prev.map(asset => 
        asset._id === assetId ? response.data.asset : asset
      ));

      // Update viewingAsset if it's currently being viewed
      if (viewingAsset && viewingAsset._id === assetId) {
        setViewingAsset(response.data.asset);
      }

      alert('Study question deleted successfully!');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete study question');
      console.error('Error deleting study question:', err);
    }
  };

  // Quiz generation functions
  const handleOpenQuizGeneration = async (assetId) => {
    // Check if quiz already exists for this asset
    try {
      const response = await assetQuizAPI.getAssetQuiz(assetId);
      if (response.data.assetQuiz) {
        setAssetQuizzes(prev => ({
          ...prev,
          [assetId]: response.data.assetQuiz
        }));
      }
    } catch (err) {
      // No quiz exists yet, which is fine
    }
    
    // Reset settings to defaults
    setQuizGenerationSettings({
      numQuestions: 5,
      questionTypes: ['MCQ', 'TrueFalse'],
      difficulties: ['Easy'],
      showAnswerAfterEach: true
    });
    
    setShowQuizGenerationModal(true);
  };

  const handleGenerateQuiz = async () => {
    if (!viewingAsset) return;
    
    // Check if asset has been saved (not a temporary ID)
    if (viewingAsset._id.startsWith('temp_')) {
      alert('Please save the course changes first before generating a quiz for this asset.');
      setShowQuizGenerationModal(false);
      return;
    }
    
    // Validate settings
    if (quizGenerationSettings.numQuestions < 1 || quizGenerationSettings.numQuestions > 20) {
      alert('Number of questions must be between 1 and 20');
      return;
    }
    
    if (quizGenerationSettings.questionTypes.length === 0) {
      alert('Please select at least one question type');
      return;
    }
    
    if (quizGenerationSettings.difficulties.length === 0) {
      alert('Please select at least one difficulty level');
      return;
    }
    
    // Close modal immediately when generation starts
    setShowQuizGenerationModal(false);
    
    try {
      setGeneratingQuiz(true);
      const response = await assetQuizAPI.generateQuiz({
        assetId: viewingAsset._id,
        numQuestions: quizGenerationSettings.numQuestions,
        questionTypes: quizGenerationSettings.questionTypes,
        difficulties: quizGenerationSettings.difficulties,
        showAnswerAfterEach: quizGenerationSettings.showAnswerAfterEach
      });
      
      // Update local state
      setAssetQuizzes(prev => ({
        ...prev,
        [viewingAsset._id]: response.data.assetQuiz
      }));
      
      alert(`Successfully generated ${response.data.assetQuiz.questions.length} quiz questions!`);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to generate quiz';
      alert(errorMsg);
      console.error('Error generating quiz:', err);
    } finally {
      setGeneratingQuiz(false);
    }
  };

  const handleDeleteQuiz = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this quiz? All student submissions will also be deleted.')) {
      return;
    }
    
    try {
      const quiz = assetQuizzes[assetId];
      if (!quiz) return;
      
      await assetQuizAPI.deleteQuiz(quiz._id);
      
      // Remove from local state
      setAssetQuizzes(prev => {
        const newState = { ...prev };
        delete newState[assetId];
        return newState;
      });
      
      alert('Quiz deleted successfully!');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete quiz');
      console.error('Error deleting quiz:', err);
    }
  };

  const handleOpenAddQuizQuestion = () => {
    if (!viewingAsset) return;
    const quiz = assetQuizzes[viewingAsset._id];
    if (!quiz) return;

    setEditingQuizQuestion(null);
    setQuizQuestionFormData({
      question: '',
      questionType: 'MCQ',
      options: ['', '', '', ''],
      correctAnswer: 0,
      explanation: '',
      difficulty: 'Medium'
    });
    setShowQuizQuestionModal(true);
  };

  const handleOpenEditQuizQuestion = (questionIndex) => {
    if (!viewingAsset) return;
    const quiz = assetQuizzes[viewingAsset._id];
    if (!quiz) return;

    const question = quiz.questions[questionIndex];
    setEditingQuizQuestion({ questionIndex, quizId: quiz._id });
    setQuizQuestionFormData({
      question: question.question,
      questionType: question.questionType,
      options: question.options || ['', ''],
      correctAnswer: question.correctAnswer,
      explanation: question.explanation || '',
      difficulty: question.difficulty
    });
    setShowQuizQuestionModal(true);
  };

  const handleSaveQuizQuestion = async () => {
    if (!viewingAsset) return;
    const quiz = assetQuizzes[viewingAsset._id];
    if (!quiz) return;

    // Validate
    if (!quizQuestionFormData.question.trim()) {
      alert('Question text is required');
      return;
    }

    if (quizQuestionFormData.questionType === 'TrueFalse') {
      if (quizQuestionFormData.options.length !== 2) {
        alert('True/False questions must have exactly 2 options');
        return;
      }
    } else if (quizQuestionFormData.questionType === 'MCQ') {
      const validOptions = quizQuestionFormData.options.filter(opt => opt.trim() !== '');
      if (validOptions.length < 2) {
        alert('MCQ must have at least 2 options');
        return;
      }
    }

    try {
      let response;
      if (editingQuizQuestion) {
        // Update existing question
        response = await assetQuizAPI.updateQuestion(
          quiz._id,
          editingQuizQuestion.questionIndex,
          quizQuestionFormData
        );
      } else {
        // Add new question
        response = await assetQuizAPI.addQuestion(quiz._id, quizQuestionFormData);
      }

      // Update local state
      setAssetQuizzes(prev => ({
        ...prev,
        [viewingAsset._id]: response.data.assetQuiz
      }));

      setShowQuizQuestionModal(false);
      alert(editingQuizQuestion ? 'Question updated successfully!' : 'Question added successfully!');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save question');
      console.error('Error saving quiz question:', err);
    }
  };

  const handleDeleteQuizQuestion = async (questionIndex) => {
    if (!viewingAsset) return;
    const quiz = assetQuizzes[viewingAsset._id];
    if (!quiz) return;

    if (!window.confirm('Are you sure you want to delete this question?')) {
      return;
    }

    try {
      const response = await assetQuizAPI.deleteQuestion(quiz._id, questionIndex);

      if (response.data.quizDeleted) {
        // Quiz was deleted because it was the last question
        setAssetQuizzes(prev => {
          const newState = { ...prev };
          delete newState[viewingAsset._id];
          return newState;
        });
        alert('Last question deleted. Quiz has been removed.');
      } else {
        // Update local state
        setAssetQuizzes(prev => ({
          ...prev,
          [viewingAsset._id]: response.data.assetQuiz
        }));
        alert('Question deleted successfully!');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete question');
      console.error('Error deleting quiz question:', err);
    }
  };


  // Assignment generation handlers
  const handleOpenGenerateAssignments = async () => {
    if (!viewingAsset) return;
    
    // Reset settings to defaults
    setAssignmentGenerationSettings({
      language: 'javascript',
      typeCounts: {
        concept_reinforcement: 0,
        debugging: 0,
        small_coding: 1,
        feature_implementation: 0,
        output_prediction: 0,
        mini_project: 0
      }
    });
    setGeneratedAssignments([]);
    setSelectedAssignments([]);
    
    // Fetch saved assignments for this asset
    if (viewingAsset._id && !viewingAsset._id.startsWith('temp_')) {
      setLoadingSavedAssignments(true);
      try {
        const response = await assignmentAPI.getLectureAssignmentsByAsset(viewingAsset._id);
        setSavedAssignmentsForAsset(response.data.assignments || []);
      } catch (err) {
        console.error('Failed to fetch saved assignments:', err);
        setSavedAssignmentsForAsset([]);
      } finally {
        setLoadingSavedAssignments(false);
      }
    } else {
      setSavedAssignmentsForAsset([]);
    }
    
    setShowAssignmentGenerationModal(true);
  };

  const handleGenerateAssignments = async () => {
    console.log('handleGenerateAssignments called');
    console.log('viewingAsset:', viewingAsset);
    console.log('assignmentGenerationSettings:', assignmentGenerationSettings);
    
    if (!viewingAsset) {
      console.log('No viewingAsset, returning');
      return;
    }
    
    // Check if asset has been saved
    if (viewingAsset._id.startsWith('temp_')) {
      alert('Please save the course changes first before generating assignments for this asset.');
      return;
    }
    
    // Calculate total assignments
    const totalAssignments = Object.values(assignmentGenerationSettings.typeCounts).reduce((sum, count) => sum + count, 0);
    
    // Validate settings
    if (totalAssignments === 0) {
      alert('Please select at least one assignment to generate');
      return;
    }
    
    if (totalAssignments > 20) {
      alert('Total number of assignments cannot exceed 20');
      return;
    }
    
    console.log('Validation passed, calling API...');
    
    try {
      setGeneratingAssignments(true);
      console.log('Making API call to generate assignments...');
      
      const response = await assignmentAPI.generateFromTranscript({
        assetId: viewingAsset._id,
        typeCounts: assignmentGenerationSettings.typeCounts,
        language: assignmentGenerationSettings.language
      });
      
      console.log('API response:', response);
      
      // Set generated assignments
      setGeneratedAssignments(response.data.assignments);
      // Select all by default
      setSelectedAssignments(response.data.assignments.map((_, idx) => idx));
      
      console.log('Generated assignments set:', response.data.assignments.length);
      
      // Keep modal open to show the results
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to generate assignments';
      alert(errorMsg);
      console.error('Error generating assignments:', err);
    } finally {
      setGeneratingAssignments(false);
    }
  };

  const handleToggleAssignmentSelection = (index) => {
    setSelectedAssignments(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleSaveSelectedAssignments = async () => {
    if (selectedAssignments.length === 0) {
      alert('Please select at least one assignment to save');
      return;
    }

    if (!viewingAsset || !viewingAsset._id) {
      alert('No asset selected');
      return;
    }

    try {
      // Get the selected assignments
      const assignmentsToSave = selectedAssignments.map(idx => generatedAssignments[idx]);
      
      console.log('Saving lecture assignments:', {
        count: assignmentsToSave.length,
        assetId: viewingAsset._id,
        language: assignmentGenerationSettings.language
      });

      // Call the bulk save API
      const response = await assignmentAPI.bulkSaveLectureAssignments({
        assignments: assignmentsToSave,
        assetId: viewingAsset._id,
        language: assignmentGenerationSettings.language,
        aiModel: 'gemini' // You can track which AI model was used
      });

      console.log('Bulk save response:', response.data);

      if (response.data.success) {
        const { savedCount, totalCount, errors } = response.data;
        
        if (savedCount === totalCount) {
          alert(`✅ Successfully saved all ${savedCount} lecture assignment(s)!`);
        } else {
          let message = `✅ Saved ${savedCount} out of ${totalCount} lecture assignments.`;
          if (errors && errors.length > 0) {
            message += `\n\n❌ Errors:\n${errors.map(e => `• ${e.title}: ${e.error}`).join('\n')}`;
          }
          alert(message);
        }
        
        // Refresh saved assignments for this asset
        try {
          const refreshResponse = await assignmentAPI.getLectureAssignmentsByAsset(viewingAsset._id);
          setSavedAssignmentsForAsset(refreshResponse.data.assignments || []);
        } catch (refreshErr) {
          console.error('Failed to refresh saved assignments:', refreshErr);
        }
        
        // Reset generated assignments
        setGeneratedAssignments([]);
        setSelectedAssignments([]);
      } else {
        alert('Failed to save lecture assignments. Please try again.');
      }

    } catch (err) {
      console.error('Error saving lecture assignments:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to save lecture assignments';
      alert(`❌ Error: ${errorMsg}`);
    }
  };

  // Problem statement generation handlers
  const handleOpenProblemStatementModal = (assignment) => {
    setSelectedAssignmentForStatement(assignment);
    setProblemStatementInput('');
    setGeneratedProblemStatement('');
    setStatementLength('');
    setShowProblemStatementModal(true);
  };

  const handleGenerateProblemStatement = async (length) => {
    if (!selectedAssignmentForStatement) return;

    setGeneratingStatement(true);
    setStatementLength(length);

    try {
      const response = await assignmentAPI.generateLectureProblemStatement(
        selectedAssignmentForStatement._id,
        length,
        problemStatementInput
      );

      setGeneratedProblemStatement(response.data.problemStatement);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to generate problem statement';
      alert(`❌ Error: ${errorMsg}`);
      console.error('Error generating problem statement:', err);
    } finally {
      setGeneratingStatement(false);
    }
  };

  const handleSaveProblemStatement = async (setAsActive = false) => {
    if (!generatedProblemStatement || !statementLength) {
      alert('Please generate a problem statement first');
      return;
    }

    try {
      const response = await assignmentAPI.saveLectureProblemStatement(
        selectedAssignmentForStatement._id,
        statementLength,
        generatedProblemStatement,
        problemStatementInput,
        setAsActive
      );

      alert(`✅ Problem statement saved successfully!${setAsActive ? ' Set as active version.' : ''}`);
      
      // Update the saved assignment in the list
      setSavedAssignmentsForAsset(prev => 
        prev.map(a => 
          a._id === selectedAssignmentForStatement._id 
            ? { ...a, ...response.data.assignment }
            : a
        )
      );
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to save problem statement';
      alert(`❌ Error: ${errorMsg}`);
      console.error('Error saving problem statement:', err);
    }
  };

  const handleCopyProblemStatement = () => {
    navigator.clipboard.writeText(generatedProblemStatement);
    alert('✅ Problem statement copied to clipboard!');
  };

  const handleSaveChanges = async () => {
    if (!hasUnsavedChanges || pendingOperations.length === 0) {
      return;
    }

    setLoading(true);
    const errors = [];
    const tempIdMap = {}; // Map temporary IDs to real IDs
    const resolveTempId = (id) => (id && tempIdMap[id] ? tempIdMap[id] : id);

    try {
      // Process operations in order: creates, updates, deletes, then reorders
      const creates = pendingOperations.filter(op => op.type.startsWith('create'));
      const updates = pendingOperations.filter(op => op.type.startsWith('update'));
      const deletes = pendingOperations.filter(op => op.type.startsWith('delete'));
      const reorders = pendingOperations.filter(op => op.type.startsWith('reorder'));

      // Process creates
      for (const op of creates) {
        try {
          if (op.type === 'createChapter') {
            const resolvedData = {
              ...op.data,
              parentChapter: resolveTempId(op.data.parentChapter) || null
            };
            const response = await chapterAPI.createChapter(resolvedData);
            tempIdMap[op.tempId] = response.data.chapter._id;
          } else if (op.type === 'createAsset') {
            const resolvedData = {
              ...op.data,
              chapter: resolveTempId(op.data.chapter) || null
            };
            const response = await learningAssetAPI.createAsset(resolvedData);
            tempIdMap[op.tempId] = response.data.asset._id;
          }
        } catch (err) {
          errors.push(`Failed to create: ${err.response?.data?.message || err.message}`);
        }
      }

      // Process updates
      for (const op of updates) {
        try {
          if (op.type === 'updateChapter') {
            const resolvedId = resolveTempId(op.id);
            if (!resolvedId || resolvedId.startsWith('temp_')) {
              continue;
            }
            const resolvedData = {
              ...op.data,
              parentChapter: resolveTempId(op.data.parentChapter) || null
            };
            await chapterAPI.updateChapter(resolvedId, resolvedData);
          } else if (op.type === 'updateAsset') {
            const resolvedId = resolveTempId(op.id);
            if (!resolvedId || resolvedId.startsWith('temp_')) {
              continue;
            }
            console.log('Updating asset:', resolvedId, 'Data:', op.data);
            const resolvedData = {
              ...op.data,
              chapter: op.data.chapter ? (resolveTempId(op.data.chapter) || op.data.chapter) : null
            };
            const response = await learningAssetAPI.updateAsset(resolvedId, resolvedData);
            console.log('Asset updated successfully:', response.data);
          }
        } catch (err) {
          console.error('Update failed:', err);
          errors.push(`Failed to update: ${err.response?.data?.message || err.message}`);
        }
      }

      // Process deletes
      for (const op of deletes) {
        try {
          if (op.type === 'deleteChapter') {
            const resolvedId = resolveTempId(op.id);
            if (!resolvedId || resolvedId.startsWith('temp_')) {
              continue;
            }
            await chapterAPI.deleteChapter(resolvedId);
          } else if (op.type === 'deleteAsset') {
            const resolvedId = resolveTempId(op.id);
            if (!resolvedId || resolvedId.startsWith('temp_')) {
              continue;
            }
            await learningAssetAPI.deleteAsset(resolvedId);
          }
        } catch (err) {
          errors.push(`Failed to delete: ${err.response?.data?.message || err.message}`);
        }
      }

      // Process reorders
      for (const op of reorders) {
        try {
          if (op.type === 'reorderChapters') {
            // Update each chapter's order
            const updatePromises = op.chapters
              .map(ch => ({
                id: resolveTempId(ch.id),
                order: ch.order
              }))
              .filter(ch => ch.id && !ch.id.startsWith('temp_'))
              .map(ch => chapterAPI.updateChapter(ch.id, { order: ch.order }));
            await Promise.all(updatePromises);
          } else if (op.type === 'reorderAssets') {
            // Update each asset's order and chapter
            const updatePromises = op.assets
              .map(asset => ({
                id: resolveTempId(asset.id),
                order: asset.order,
                chapter: resolveTempId(asset.chapter) || null
              }))
              .filter(asset => asset.id && !asset.id.startsWith('temp_'))
              .map(asset => learningAssetAPI.updateAsset(asset.id, {
                order: asset.order,
                chapter: asset.chapter
              }));
            await Promise.all(updatePromises);
          }
        } catch (err) {
          errors.push(`Failed to reorder: ${err.response?.data?.message || err.message}`);
        }
      }

      if (errors.length > 0) {
        setError('Some changes failed to save: ' + errors.join(', '));
        setLoading(false);
      } else {
        setError('');
        // Close viewing asset modal before refresh to prevent stale reference
        setViewingAsset(null);
        setEditingTranscriptTitle(false);
        setTranscriptTitleValue('');
        // Refresh course data from server - this will manage loading state
        await fetchCourseDetails();
      }
      
      // Clear pending operations
      setPendingOperations([]);
      setHasUnsavedChanges(false);
    } catch (err) {
      setError('Failed to save changes: ' + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };

  const handleDiscardChanges = () => {
    if (!hasUnsavedChanges) return;
    
    if (!window.confirm('Discard all unsaved changes? This cannot be undone.')) {
      return;
    }

    // Restore original data
    setChapters(JSON.parse(JSON.stringify(originalChapters)));
    setAssets(JSON.parse(JSON.stringify(originalAssets)));
    setPendingOperations([]);
    setHasUnsavedChanges(false);
    setError('');
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    // Check if we're dragging a chapter or an asset
    const activeAsset = assets.find(a => a._id === active.id);
    const activeChapter = chapters.find(c => c._id === active.id);

    if (activeAsset) {
      // Handle asset drag
      const overAsset = assets.find(a => a._id === over.id);
      if (!overAsset) return;

      const oldChapterId = activeAsset.chapter?._id || activeAsset.chapter;
      const newChapterId = overAsset.chapter?._id || overAsset.chapter;

      const activeIndex = assets.findIndex(a => a._id === active.id);
      const overIndex = assets.findIndex(a => a._id === over.id);

      // Use arrayMove for proper reordering
      let updatedAssets = arrayMove(assets, activeIndex, overIndex);
      
      // If moving to a different chapter, update the chapter reference
      if (oldChapterId !== newChapterId) {
        updatedAssets = updatedAssets.map(a => 
          a._id === active.id ? { ...a, chapter: newChapterId } : a
        );
      }

      // Update state immediately for smooth UX
      setAssets(updatedAssets);

      // Track reorder operation
      setPendingOperations(prev => [...prev, {
        type: 'reorderAssets',
        assets: updatedAssets.map((a, index) => ({
          id: a._id,
          chapter: a.chapter?._id || a.chapter,
          order: index
        }))
      }]);
      setHasUnsavedChanges(true);
    } else if (activeChapter) {
      // Handle chapter drag
      const overChapter = chapters.find(c => c._id === over.id);
      if (!overChapter) return;

      // Only allow reordering chapters at the same level
      if (activeChapter.parentChapter !== overChapter.parentChapter) return;

      // Optimistically update the UI
      const updatedChapters = [...chapters];
      const activeIndex = updatedChapters.findIndex(c => c._id === active.id);
      const overIndex = updatedChapters.findIndex(c => c._id === over.id);

      const [movedChapter] = updatedChapters.splice(activeIndex, 1);
      updatedChapters.splice(overIndex, 0, movedChapter);

      setChapters(updatedChapters);

      // Track reorder operation
      setPendingOperations(prev => [...prev, {
        type: 'reorderChapters',
        chapters: updatedChapters.map((ch, index) => ({
          id: ch._id,
          order: index
        }))
      }]);
      setHasUnsavedChanges(true);
    }
  };

  // Sortable Asset Component
  const SortableAsset = ({ asset }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: asset._id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      marginBottom: '8px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px',
      background: '#ffffff',
      borderRadius: '6px',
      border: '1px solid #e2e8f0',
      cursor: 'default',
    };

    return (
      <div ref={setNodeRef} style={style}>
        <div
          {...attributes}
          {...listeners}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '22px',
            height: '22px',
            marginRight: '8px',
            color: '#94a3b8',
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
            fontSize: '14px',
            lineHeight: 1
          }}
          title="Drag to reorder"
        >
          ⋮⋮
        </div>
        <div 
          style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}
          onClick={(e) => {
            // Allow viewing asset when not dragging
            if (!isDragging) {
              setViewingAsset(asset);
            }
          }}
        >
          <span style={{ fontSize: '16px' }}>
            {asset.assetType === 'video' && '▶️'}
            {asset.assetType === 'document' && '📄'}
            {asset.assetType === 'quiz' && '📝'}
            {asset.assetType === 'flashcard' && '🎴'}
            {asset.assetType === 'interview' && '🎤'}
            {asset.assetType === 'link' && '🔗'}
            {asset.assetType === 'note' && '📋'}
            {asset.assetType === 'markdown' && '📝'}
            {asset.assetType === 'transcript' && '📝'}
          </span>
          <span style={{ fontSize: '13px', color: '#1a202c', flex: 1 }}>{asset.title}</span>
          {asset.isRequired && (
            <span style={{ fontSize: '10px', padding: '2px 6px', background: '#f56565', color: '#fff', borderRadius: '4px' }}>
              Required
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {/* Q&A Indicator */}
          {asset.studyQuestions && asset.studyQuestions.length > 0 && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              padding: '2px 6px',
              background: '#dcfce7',
              color: '#166534',
              borderRadius: '10px',
              fontSize: '10px',
              fontWeight: '600'
            }}>
              💬 {asset.studyQuestions.length}
            </span>
          )}
          {/* Quiz Indicator */}
          {assetQuizzes[asset._id] && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              padding: '2px 6px',
              background: '#dbeafe',
              color: '#1e40af',
              borderRadius: '10px',
              fontSize: '10px',
              fontWeight: '600'
            }}>
              📝 {assetQuizzes[asset._id].questions.length}
            </span>
          )}
          {/* Teaching Analysis Button for Transcript Assets */}
          {asset.assetType === 'transcript' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setTeachingAnalysisAsset(asset);
                setShowTeachingAnalysis(true);
              }}
              style={{
                padding: '2px 8px',
                fontSize: '10px',
                background: '#764ba2',
                border: 'none',
                borderRadius: '4px',
                color: '#fff',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px'
              }}
              title="Analyze Teaching Quality"
            >
              🎓 Analysis
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteAsset(asset._id);
            }}
            style={{ padding: '2px 6px', fontSize: '10px', background: '#f56565', border: 'none', borderRadius: '4px', color: '#fff', cursor: 'pointer' }}
          >
            Delete
          </button>
        </div>
      </div>
    );
  };

  // Sortable Chapter Component
  const SortableChapter = ({ chapter, level }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: chapter._id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      marginLeft: `${level * 24}px`,
      marginBottom: '12px',
    };

    return (
      <div ref={setNodeRef} style={style}>
        {renderChapterContent(chapter, level, attributes, listeners, isDragging)}
      </div>
    );
  };

  const renderChapterContent = (chapter, level, dragAttributes, dragListeners, isDragging) => {
    const chapterAssets = assets.filter(a => a.chapter?._id === chapter._id || a.chapter === chapter._id);
    const subChapters = getSortedChapters(chapter.subChapters || []);
    
    console.log(`Chapter "${chapter.title}" - Total assets: ${chapterAssets.length}, Transcripts: ${chapterAssets.filter(a => a.assetType === 'transcript').length}`);

    return (
      <div>
        <div style={{
          padding: '12px',
          background: '#f7fafc',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          cursor: isDragging ? 'grabbing' : 'default',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span 
                {...dragAttributes} 
                {...dragListeners}
                style={{ 
                  cursor: 'grab', 
                  fontSize: '16px',
                  padding: '4px',
                  display: 'inline-flex',
                  alignItems: 'center'
                }}
                title="Drag to reorder"
              >
                ⋮⋮
              </span>
              <div>
                <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', color: '#2d3748', fontWeight: '600' }}>
                  {level === 0 ? '📖' : '📄'} {chapter.title}
                </h4>
                {chapter.description && (
                  <p style={{ margin: 0, fontSize: '13px', color: '#718096' }}>{chapter.description}</p>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => {
                  setEditingChapter(chapter);
                  setChapterForm({
                    title: chapter.title,
                    description: chapter.description || '',
                    content: chapter.content || '',
                    parentChapter: chapter.parentChapter || null,
                    order: chapter.order || 0
                  });
                  setShowChapterModal(true);
                }}
                style={{ padding: '4px 8px', fontSize: '11px', background: '#48bb78', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer' }}
              >
                Edit
              </button>
              <button
                onClick={() => {
                  setSelectedChapter(chapter._id);
                  setShowChapterModal(true);
                }}
                style={{ padding: '4px 8px', fontSize: '11px', background: '#718096', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer' }}
              >
                + Sub-chapter
              </button>
              <button
                onClick={() => {
                  setSelectedChapter(chapter._id);
                  setShowAssetModal(true);
                }}
                style={{ padding: '4px 8px', fontSize: '11px', background: '#667eea', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer' }}
              >
                + Asset
              </button>
              <button
                onClick={() => handleDeleteChapter(chapter._id)}
                style={{ padding: '4px 8px', fontSize: '11px', background: '#f56565', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer' }}
              >
                Delete
              </button>
            </div>
          </div>

          {/* Chapter Assets with Drag and Drop */}
          {chapterAssets.length > 0 && (
            <div style={{ marginTop: '12px', paddingLeft: '16px', borderLeft: '2px solid #cbd5e0' }}>
              <SortableContext items={chapterAssets.map(a => a._id)} strategy={verticalListSortingStrategy}>
                {chapterAssets.map(asset => (
                  <SortableAsset key={asset._id} asset={asset} />
                ))}
              </SortableContext>
            </div>
          )}
        </div>

        {/* Sub-chapters */}
        {subChapters.length > 0 && (
          <div style={{ marginTop: '8px' }}>
            <SortableContext items={subChapters.map(c => c._id)} strategy={verticalListSortingStrategy}>
              {subChapters.map(sub => (
                <SortableChapter key={sub._id} chapter={sub} level={level + 1} />
              ))}
            </SortableContext>
          </div>
        )}
      </div>
    );
  };

  const renderChapter = (chapter, level = 0) => {
    return <SortableChapter key={chapter._id} chapter={chapter} level={level} />;
  };

  if (loading) {
    return <div className="container" style={{ paddingTop: '20px', background: '#ffffff', minHeight: '100vh', borderRadius: '12px', padding: '24px', textAlign: 'center', color: '#1a202c' }}>Loading course...</div>;
  }

  if (!course) {
    return <div className="container" style={{ paddingTop: '20px', background: '#ffffff', minHeight: '100vh', borderRadius: '12px', padding: '24px', textAlign: 'center', color: '#1a202c' }}>Course not found</div>;
  }

  return (
    <>
      <div className="container" style={{ paddingTop: '20px', background: '#ffffff', minHeight: '100vh', borderRadius: '12px', padding: '24px' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
          <button
            onClick={() => navigate('/courses')}
            className="btn btn-secondary"
            style={{ fontSize: '13px' }}
          >
            ← Back to Courses
          </button>
          <button
            onClick={() => navigate(`/course-settings/${id}`)}
            className="btn"
            style={{ 
              fontSize: '13px',
              background: '#f7fafc',
              border: '1px solid #e2e8f0',
              color: '#4a5568'
            }}
          >
            ⚙️ Course Settings
          </button>
          <button
            onClick={() => {
              // Open course preview in a new tab to show how students see it
              window.open(`/course/${id}`, '_blank');
            }}
            className="btn"
            style={{ 
              fontSize: '13px',
              background: '#667eea',
              border: '1px solid #667eea',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            title="Preview how students see this course"
          >
            👁️ Preview Course
          </button>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div>
            <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>
              {course.title}
            </h2>
            <p style={{ color: '#718096', fontSize: '14px', margin: 0 }}>
              Code: <span style={{ fontWeight: '600', color: '#667eea' }}>{course.code}</span>
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {hasUnsavedChanges && (
              <>
                <span style={{ 
                  color: '#d69e2e', 
                  fontSize: '13px', 
                  fontWeight: '600',
                  backgroundColor: '#fef3c7',
                  padding: '4px 10px',
                  borderRadius: '4px'
                }}>
                  {pendingOperations.length} unsaved {pendingOperations.length === 1 ? 'change' : 'changes'}
                </span>
                <button
                  onClick={handleDiscardChanges}
                  className="btn btn-secondary"
                  style={{ fontSize: '14px', padding: '8px 16px' }}
                >
                  Discard Changes
                </button>
                <button
                  onClick={handleSaveChanges}
                  className="btn btn-primary"
                  style={{ fontSize: '14px', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px' }}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : '💾 Save Changes'}
                </button>
              </>
            )}
            
            <span style={{
              padding: '6px 12px',
              background: course.status === 'published' ? '#c6f6d5' : '#fef3c7',
              color: course.status === 'published' ? '#22543d' : '#92400e',
              borderRadius: '5px',
              fontSize: '13px',
              fontWeight: '600',
              textTransform: 'capitalize'
            }}>
              {course.status}
            </span>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Tabs */}
      <div style={{ borderBottom: '2px solid #e2e8f0', marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '24px' }}>
          {['structure', 'assets', 'ai-questions', 'lecture-assignments'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '12px 0',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab ? '2px solid #667eea' : '2px solid transparent',
                color: activeTab === tab ? '#667eea' : '#a0aec0',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                textTransform: 'capitalize',
                marginBottom: '-2px'
              }}
            >
              {tab === 'ai-questions' ? 'AI Questions' : tab === 'lecture-assignments' ? 'Lecture Assignments' : tab}
            </button>
          ))}
        </div>
      </div>

      {/* Structure Tab */}
      {activeTab === 'structure' && (
        <div>
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <h3 style={{ fontSize: '16px', color: '#1a202c', margin: 0 }}>Course Structure</h3>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <select
                value={chapterSortOrder}
                onChange={(e) => handleChapterSortChange(e.target.value)}
                style={{
                  padding: '8px 12px',
                  fontSize: '13px',
                  background: '#ffffff',
                  color: '#1a202c',
                  border: '1px solid #cbd5e0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                <option value="old-new">📅 Oldest First</option>
                <option value="new-old">🕐 Newest First</option>
              </select>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setSelectedChapter(null);
                  setShowChapterModal(true);
                }}
                style={{ fontSize: '13px', padding: '8px 16px' }}
              >
                + Add Chapter
              </button>
            </div>
          </div>

          {chapters.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', background: '#ffffff', borderRadius: '12px', border: '2px dashed #cbd5e0' }}>
              <p style={{ fontSize: '36px', margin: '0 0 12px 0' }}>📖</p>
              <p style={{ color: '#718096', margin: 0 }}>No chapters yet. Create your first chapter to start building the course.</p>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext 
                items={getSortedChapters(chapters.filter(ch => !ch.parentChapter)).map(ch => ch._id)} 
                strategy={verticalListSortingStrategy}
              >
                {getSortedChapters(chapters.filter(ch => !ch.parentChapter)).map(chapter => renderChapter(chapter))}
              </SortableContext>
            </DndContext>
          )}

          {/* Unassigned assets (no chapter) */}
          {assets.filter(a => !a.chapter || !(a.chapter?._id || a.chapter)).length > 0 && (
            <div style={{
              marginTop: '20px',
              padding: '12px',
              background: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid #e2e8f0'
            }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#2d3748' }}>
                📂 Unassigned Assets ({assets.filter(a => !a.chapter || !(a.chapter?._id || a.chapter)).length})
              </h4>
              <div>
                {assets
                  .filter(a => !a.chapter || !(a.chapter?._id || a.chapter))
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map(asset => (
                    <div key={asset._id} style={{ marginBottom: '8px' }}>
                      <SortableAsset asset={asset} />
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Assets Tab */}
      {activeTab === 'assets' && (
        <div>
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
            <h3 style={{ fontSize: '16px', color: '#1a202c', margin: 0 }}>All Learning Assets ({assets.filter(a => assetTypeFilter === 'all' || a.assetType === assetTypeFilter).length})</h3>
            <button
              className="btn btn-primary"
              onClick={() => {
                setSelectedChapter(null);
                setShowAssetModal(true);
              }}
              style={{ fontSize: '13px', padding: '8px 16px' }}
            >
              + Add Asset
            </button>
          </div>

          {/* Asset Type Filter */}
          <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[
              { value: 'all', label: 'All', icon: '📚' },
              { value: 'note', label: 'Note', icon: '📋' },
              { value: 'video', label: 'Video', icon: '▶️' },
              { value: 'document', label: 'Document', icon: '📄' },
              { value: 'quiz', label: 'Quiz', icon: '📝' },
              { value: 'flashcard', label: 'Flashcard', icon: '🎴' },
              { value: 'interview', label: 'Interview', icon: '🎤' },
              { value: 'link', label: 'Link', icon: '🔗' },
              { value: 'transcript', label: 'Transcript', icon: '📝' }
            ].map(filter => {
              const count = filter.value === 'all' 
                ? assets.length 
                : assets.filter(a => a.assetType === filter.value).length;
              return (
                <button
                  key={filter.value}
                  onClick={() => setAssetTypeFilter(filter.value)}
                  style={{
                    padding: '8px 14px',
                    fontSize: '13px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    background: assetTypeFilter === filter.value ? '#667eea' : '#ffffff',
                    color: assetTypeFilter === filter.value ? '#ffffff' : '#4a5568',
                    cursor: 'pointer',
                    fontWeight: assetTypeFilter === filter.value ? '600' : '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <span>{filter.icon}</span>
                  <span>{filter.label}</span>
                  <span style={{ 
                    fontSize: '11px', 
                    background: assetTypeFilter === filter.value ? 'rgba(255,255,255,0.2)' : '#e2e8f0',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    fontWeight: '600'
                  }}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div style={{ display: 'grid', gap: '12px' }}>
            {assets.filter(asset => assetTypeFilter === 'all' || asset.assetType === assetTypeFilter).map(asset => (
              <div 
                key={asset._id} 
                style={{ 
                  padding: '12px', 
                  background: '#f7fafc', 
                  borderRadius: '12px', 
                  border: '1px solid #e2e8f0',
                  cursor: 'pointer'
                }}
                onClick={() => setViewingAsset(asset)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>
                      {asset.assetType === 'video' && '▶️'}
                      {asset.assetType === 'document' && '📄'}
                      {asset.assetType === 'quiz' && '📝'}
                      {asset.assetType === 'flashcard' && '🎴'}
                      {asset.assetType === 'interview' && '🎤'}
                      {asset.assetType === 'link' && '🔗'}
                      {asset.assetType === 'note' && '📋'}
                      {asset.assetType === 'markdown' && '📝'}
                      {asset.assetType === 'transcript' && '📝'}
                    </span>
                    <div>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', color: '#1a202c' }}>{asset.title}</h4>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <p style={{ margin: 0, fontSize: '12px', color: '#718096' }}>
                          {asset.chapter ? `Chapter: ${asset.chapter.title}` : 'Course level'} • {asset.assetType}
                        </p>
                        {/* Q&A Indicator */}
                        {asset.studyQuestions && asset.studyQuestions.length > 0 && (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '2px 8px',
                            background: '#dcfce7',
                            color: '#166534',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}>
                            💬 Q&A ({asset.studyQuestions.length})
                          </span>
                        )}
                        {/* Quiz Indicator */}
                        {assetQuizzes[asset._id] && (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '2px 8px',
                            background: '#dbeafe',
                            color: '#1e40af',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}>
                            📝 Quiz ({assetQuizzes[asset._id].questions.length})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteAsset(asset._id);
                    }}
                    style={{ padding: '6px 12px', fontSize: '12px', background: '#f56565', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}



        {/* AI Questions Tab */}
        {activeTab === 'ai-questions' && (
          <div>
            <CourseAIQuestions courseId={id} />
          </div>
        )}

        {/* Lecture Assignments Tab */}
        {activeTab === 'lecture-assignments' && (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1a202c', margin: 0 }}>
                  📝 Lecture Assignments
                </h3>
                <div style={{ fontSize: '14px', color: '#64748b' }}>
                  {lectureAssignments.length} assignment{lectureAssignments.length !== 1 ? 's' : ''}
                </div>
              </div>

              {/* Filters */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '12px', 
                marginBottom: '20px',
                padding: '16px',
                background: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                    Type
                  </label>
                  <select
                    value={lectureAssignmentFilters.type}
                    onChange={(e) => setLectureAssignmentFilters(prev => ({ ...prev, type: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      background: '#ffffff',
                      color: '#1e293b',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">All Types</option>
                    <option value="concept_reinforcement">📚 Concept Reinforcement</option>
                    <option value="debugging">🐛 Debugging</option>
                    <option value="small_coding">💻 Small Coding</option>
                    <option value="feature_implementation">⚙️ Feature Implementation</option>
                    <option value="output_prediction">🔮 Output Prediction</option>
                    <option value="mini_project">🚀 Mini Project</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                    Complexity
                  </label>
                  <select
                    value={lectureAssignmentFilters.complexity}
                    onChange={(e) => setLectureAssignmentFilters(prev => ({ ...prev, complexity: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      background: '#ffffff',
                      color: '#1e293b',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">All Complexities</option>
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                    Language
                  </label>
                  <select
                    value={lectureAssignmentFilters.language}
                    onChange={(e) => setLectureAssignmentFilters(prev => ({ ...prev, language: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      background: '#ffffff',
                      color: '#1e293b',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">All Languages</option>
                    <option value="javascript">JavaScript</option>
                    <option value="python">Python</option>
                    <option value="java">Java</option>
                    <option value="sql">SQL</option>
                    <option value="csharp">C#</option>
                    <option value="typescript">TypeScript</option>
                  </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setLectureAssignmentFilters({ type: '', complexity: '', language: '', search: '' });
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      fontWeight: '600',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      background: '#ffffff',
                      color: '#64748b',
                      cursor: 'pointer'
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>

              {/* Loading State */}
              {loadingLectureAssignments && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>⟳</div>
                  <div>Loading assignments...</div>
                </div>
              )}

              {/* Empty State */}
              {!loadingLectureAssignments && lectureAssignments.length === 0 && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px',
                  background: '#f8fafc',
                  borderRadius: '12px',
                  border: '2px dashed #cbd5e1'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>
                    No Lecture Assignments Found
                  </div>
                  <div style={{ fontSize: '14px', color: '#64748b' }}>
                    Generate assignments from session transcripts to create practice exercises for students.
                  </div>
                </div>
              )}

              {/* Assignments Grid */}
              {!loadingLectureAssignments && lectureAssignments.length > 0 && (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
                  gap: '16px' 
                }}>
                  {lectureAssignments.map(assignment => (
                    <div
                      key={assignment._id}
                      style={{
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        padding: '20px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                      }}
                      onClick={() => setViewingLectureAssignment(assignment)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      {/* Header */}
                      <div style={{ marginBottom: '12px' }}>
                        <h4 style={{ 
                          fontSize: '15px', 
                          fontWeight: '700', 
                          color: '#1e293b', 
                          margin: '0 0 8px 0',
                          lineHeight: '1.4'
                        }}>
                          {assignment.title}
                        </h4>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <span style={{
                            fontSize: '11px',
                            fontWeight: '600',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            background: assignment.type === 'mini_project' ? '#fef3c7' : 
                                       assignment.type === 'debugging' ? '#fee2e2' :
                                       assignment.type === 'feature_implementation' ? '#ddd6fe' :
                                       assignment.type === 'small_coding' ? '#dbeafe' :
                                       assignment.type === 'output_prediction' ? '#cffafe' :
                                       '#d1fae5',
                            color: assignment.type === 'mini_project' ? '#92400e' : 
                                   assignment.type === 'debugging' ? '#991b1b' :
                                   assignment.type === 'feature_implementation' ? '#5b21b6' :
                                   assignment.type === 'small_coding' ? '#1e40af' :
                                   assignment.type === 'output_prediction' ? '#155e75' :
                                   '#065f46'
                          }}>
                            {assignment.type.replace(/_/g, ' ').toUpperCase()}
                          </span>
                          <span style={{
                            fontSize: '11px',
                            fontWeight: '600',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            background: assignment.complexity === 'Easy' ? '#d1fae5' : 
                                       assignment.complexity === 'Medium' ? '#fef3c7' : '#fee2e2',
                            color: assignment.complexity === 'Easy' ? '#065f46' : 
                                   assignment.complexity === 'Medium' ? '#92400e' : '#991b1b'
                          }}>
                            {assignment.complexity}
                          </span>
                          <span style={{
                            fontSize: '11px',
                            fontWeight: '600',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            background: '#f1f5f9',
                            color: '#475569'
                          }}>
                            {assignment.language.toUpperCase()}
                          </span>
                        </div>
                      </div>

                      {/* Description Preview */}
                      <p style={{ 
                        fontSize: '13px', 
                        color: '#64748b', 
                        margin: '0 0 12px 0',
                        lineHeight: '1.5',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {assignment.description}
                      </p>

                      {/* Footer Info */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        paddingTop: '12px',
                        borderTop: '1px solid #f1f5f9'
                      }}>
                        <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                          ⏱️ {assignment.estimatedTime}
                        </div>
                        <div style={{ fontSize: '12px', color: '#3b82f6', fontWeight: '600' }}>
                          View Details →
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Chapter Modal */}
      <Modal
        open={showChapterModal}
        onClose={handleChapterModalClose}
        aria-labelledby="chapter-modal-title"
        disableScrollLock={true}
        BackdropProps={{ timeout: 0 }}
      >
        <Box sx={{
          position: 'absolute',
          top: '5%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: { xs: '95%', sm: '90%', md: '500px' },
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          borderRadius: '12px',
          boxShadow: 24,
          p: 3,
          overflowY: 'auto',
          outline: 'none'
        }}>
          <IconButton
            onClick={handleChapterModalClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500',
              '&:hover': {
                color: 'grey.700',
                bgcolor: 'grey.100'
              }
            }}
          >
            <CloseIcon />
          </IconButton>

          {!createdChapter ? (
            <>
              <h2 id="chapter-modal-title" style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#1a202c' }}>
                {editingChapter ? 'Edit Chapter' : selectedChapter ? 'Add Sub-chapter' : 'Add Chapter'}
              </h2>
              <form onSubmit={handleCreateChapter}>
                <div className="form-group">
                  <label style={{ color: '#1a202c' }}>Title *</label>
                  <input
                    type="text"
                    required
                    value={chapterForm.title}
                    onChange={(e) => setChapterForm({ ...chapterForm, title: e.target.value })}
                    style={{ background: '#ffffff', color: '#1a202c', border: '1px solid #e2e8f0' }}
                  />
                </div>
                <div className="form-group">
                  <label style={{ color: '#1a202c' }}>Description</label>
                  <textarea
                    value={chapterForm.description}
                    onChange={(e) => setChapterForm({ ...chapterForm, description: e.target.value })}
                    rows="2"
                    style={{ background: '#ffffff', color: '#1a202c', border: '1px solid #e2e8f0' }}
                  />
                </div>
                <div className="form-group">
                  <label style={{ color: '#1a202c' }}>Content (Notes/Instructions)</label>
                  <textarea
                    value={chapterForm.content}
                    onChange={(e) => setChapterForm({ ...chapterForm, content: e.target.value })}
                    rows="4"
                    style={{ background: '#ffffff', color: '#1a202c', border: '1px solid #e2e8f0' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={handleChapterModalClose}>Cancel</button>
                  <button type="submit" className="btn btn-primary">{editingChapter ? 'Update' : 'Create'}</button>
                </div>
              </form>
            </>
          ) : (
            <>
              <h2 id="chapter-modal-title" style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#1a202c' }}>
                Chapter {editingChapter ? 'Updated' : 'Created'} Successfully!
              </h2>
              <div style={{ 
                padding: '16px', 
                marginBottom: '20px', 
                background: '#f0fdf4', 
                border: '1px solid #86efac', 
                borderRadius: '8px' 
              }}>
                <div style={{ fontWeight: '600', color: '#166534', marginBottom: '4px' }}>
                  {createdChapter.title}
                </div>
                {createdChapter.description && (
                  <div style={{ fontSize: '13px', color: '#4b5563' }}>
                    {createdChapter.description}
                  </div>
                )}
              </div>
              
              <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#4b5563' }}>
                What would you like to do next?
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button 
                  onClick={handleChapterModalEdit}
                  className="btn btn-secondary"
                  style={{ 
                    width: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <span>✏️</span>
                  <span>Edit Chapter</span>
                </button>
                
                <button 
                  onClick={handleChapterModalAddSubChapter}
                  className="btn btn-secondary"
                  style={{ 
                    width: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <span>📑</span>
                  <span>Add Sub-chapter</span>
                </button>
                
                <button 
                  onClick={handleChapterModalAddAsset}
                  className="btn btn-primary"
                  style={{ 
                    width: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <span>📝</span>
                  <span>Add Asset</span>
                </button>
                
                <button 
                  onClick={handleChapterModalDelete}
                  className="btn"
                  style={{ 
                    width: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '8px',
                    background: '#fee2e2',
                    color: '#991b1b',
                    border: '1px solid #fecaca'
                  }}
                >
                  <span>🗑️</span>
                  <span>Delete Chapter</span>
                </button>

                <button 
                  onClick={handleChapterModalClose}
                  className="btn btn-secondary"
                  style={{ 
                    width: '100%',
                    marginTop: '10px'
                  }}
                >
                  Done
                </button>
              </div>
            </>
          )}
        </Box>
      </Modal>

      {/* Create Asset Modal */}
      <Modal
        open={showAssetModal}
        onClose={handleAssetModalClose}
        aria-labelledby="asset-modal-title"
        disableScrollLock={true}
        BackdropProps={{ timeout: 0 }}
      >
        <Box sx={{
          position: 'absolute',
          top: '5%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: { xs: '95%', sm: '90%', md: editingAsset ? '500px' : '700px' },
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          borderRadius: '12px',
          boxShadow: 24,
          p: 3,
          overflowY: 'auto',
          outline: 'none'
        }}>
          <IconButton
            onClick={handleAssetModalClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500',
              '&:hover': {
                color: 'grey.700',
                bgcolor: 'grey.100'
              }
            }}
          >
            <CloseIcon />
          </IconButton>

          <h2 id="asset-modal-title" style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#1a202c' }}>
            {editingAsset ? 'Edit Learning Asset' : 'Learning Asset'}
          </h2>
            
          {!createdAsset ? (
            <>
            {/* Tabs (only show if not editing) */}
            {!editingAsset && (
              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                marginBottom: '20px', 
                borderBottom: '2px solid #e2e8f0' 
              }}>
                <button
                  type="button"
                  onClick={() => setAssetModalTab('add')}
                  style={{
                    padding: '10px 20px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: assetModalTab === 'add' ? '2px solid #667eea' : '2px solid transparent',
                    color: assetModalTab === 'add' ? '#667eea' : '#4a5568',
                    fontWeight: assetModalTab === 'add' ? '600' : '500',
                    cursor: 'pointer',
                    fontSize: '14px',
                    marginBottom: '-2px'
                  }}
                >
                  ➕ Add New Asset
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAssetModalTab('import');
                    if (importCourseOptions.length === 0) {
                      fetchImportCourses();
                    }
                  }}
                  style={{
                    padding: '10px 20px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: assetModalTab === 'import' ? '2px solid #667eea' : '2px solid transparent',
                    color: assetModalTab === 'import' ? '#667eea' : '#4a5568',
                    fontWeight: assetModalTab === 'import' ? '600' : '500',
                    cursor: 'pointer',
                    fontSize: '14px',
                    marginBottom: '-2px'
                  }}
                >
                  📥 Import Existing Asset
                </button>
              </div>
            )}

            {/* Add Asset Tab Content */}
            {assetModalTab === 'add' && (
            <form onSubmit={handleCreateAsset}>
              <div className="form-group">
                <label style={{ color: '#1a202c' }}>Title *</label>
                <input
                  type="text"
                  required
                  value={assetForm.title}
                  onChange={(e) => setAssetForm({ ...assetForm, title: e.target.value })}
                  style={{ background: '#ffffff', color: '#1a202c', border: '1px solid #e2e8f0' }}
                />
              </div>
              <div className="form-group">
                <label style={{ color: '#1a202c' }}>Type *</label>
                <select
                  value={assetForm.assetType}
                  onChange={(e) => setAssetForm({ ...assetForm, assetType: e.target.value })}
                  style={{ background: '#ffffff', color: '#1a202c', border: '1px solid #e2e8f0' }}
                >
                  <option value="note">📋 Note</option>
                  <option value="link">🔗 External Link</option>
                  <option value="video">🎥 Video</option>
                  <option value="document">📄 Document</option>
                  <option value="markdown">📝 Markdown (from PDF)</option>
                  <option value="transcript">📄 Transcript</option>
                  <option value="quiz">📝 Quiz</option>
                  <option value="flashcard">🎴 Flashcard</option>
                  <option value="interview">🎤 Interview</option>
                </select>
              </div>
              {assetForm.assetType === 'note' && (
                <div className="form-group">
                  <label style={{ color: '#1a202c' }}>Content</label>
                  <RichTextEditor
                    value={assetForm.content}
                    onChange={(newContent) => setAssetForm({ ...assetForm, content: newContent })}
                  />
                </div>
              )}
              {assetForm.assetType === 'link' && (
                <div className="form-group">
                  <label style={{ color: '#1a202c' }}>URL</label>
                  <input
                    type="url"
                    value={assetForm.externalUrl}
                    onChange={(e) => setAssetForm({ ...assetForm, externalUrl: e.target.value })}
                    style={{ background: '#ffffff', color: '#1a202c', border: '1px solid #e2e8f0' }}
                  />
                </div>
              )}
              {assetForm.assetType === 'video' && (
                <div className="form-group">
                  <label style={{ color: '#1a202c', display: 'block', marginBottom: '12px' }}>Video Source</label>
                  <div style={{ display: 'flex', gap: '20px', marginBottom: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="videoSource"
                        checked={videoSourceType === 'upload'}
                        onChange={() => {
                          setVideoSourceType('upload');
                          setAssetForm({ ...assetForm, externalUrl: '' });
                        }}
                      />
                      <span style={{ color: '#1a202c' }}>Upload File</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="videoSource"
                        checked={videoSourceType === 'url'}
                        onChange={() => {
                          setVideoSourceType('url');
                          setAssetForm({ ...assetForm, fileUrl: '', fileType: '', fileName: '', fileSize: 0 });
                        }}
                      />
                      <span style={{ color: '#1a202c' }}>YouTube/Vimeo URL</span>
                    </label>
                  </div>

                  {videoSourceType === 'upload' ? (
                    <div>
                      {assetForm.fileUrl ? (
                        <div style={{ padding: '12px', background: '#f7fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                          <p style={{ margin: '0 0 8px 0', color: '#4a5568', fontSize: '14px' }}>
                            ✓ {assetForm.fileName} ({(assetForm.fileSize / 1024 / 1024).toFixed(2)} MB)
                          </p>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setAssetForm({ ...assetForm, fileUrl: '', fileType: '', fileName: '', fileSize: 0, estimatedDuration: null })}
                            style={{ fontSize: '13px', padding: '6px 12px' }}
                          >
                            Remove File
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => setShowFileUploadModal(true)}
                        >
                          Upload Video
                        </button>
                      )}
                    </div>
                  ) : (
                    <div>
                      <input
                        type="url"
                        placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
                        value={assetForm.externalUrl}
                        onChange={(e) => handleExternalUrlChange(e.target.value)}
                        style={{ 
                          background: '#ffffff', 
                          color: '#1a202c', 
                          border: '1px solid #e2e8f0',
                          width: '100%',
                          padding: '10px'
                        }}
                      />
                      <small style={{ display: 'block', marginTop: '6px', color: '#718096', fontSize: '12px' }}>
                        Paste a YouTube or Vimeo video URL (e.g., https://www.youtube.com/watch?v=dQw4w9WgXcQ)
                      </small>
                      {fetchingVideoDuration && (
                        <div style={{ marginTop: '8px', fontSize: '13px', color: '#667eea', fontWeight: '500' }}>
                          ⏳ Fetching video duration...
                        </div>
                      )}
                      {assetForm.estimatedDuration && !fetchingVideoDuration && assetForm.externalUrl && (
                        <div style={{ marginTop: '8px', fontSize: '13px', color: '#48bb78', fontWeight: '600' }}>
                          ✓ Duration detected: {Math.floor(assetForm.estimatedDuration)} minutes
                        </div>
                      )}
                      <div style={{ 
                        marginTop: '12px', 
                        padding: '12px', 
                        background: '#fff3cd', 
                        border: '1px solid #ffc107',
                        borderRadius: '6px'
                      }}>
                        <p style={{ margin: '0 0 8px 0', color: '#856404', fontSize: '13px', fontWeight: '600' }}>
                          ⚠️ Important for Vimeo videos:
                        </p>
                        <ul style={{ margin: 0, paddingLeft: '20px', color: '#856404', fontSize: '12px' }}>
                          <li>Set "Who can watch" to <strong>"Anyone"</strong> or <strong>"Anyone with the link"</strong></li>
                          <li>Set "Where can this be embedded" to <strong>"Anywhere"</strong></li>
                          <li>Otherwise, students will see a thumbnail but video won't play</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {assetForm.assetType === 'document' && (
                <div className="form-group">
                  <label style={{ color: '#1a202c' }}>File Upload</label>
                  {assetForm.fileUrl ? (
                    <div style={{ padding: '12px', background: '#f7fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <p style={{ margin: '0 0 8px 0', color: '#4a5568', fontSize: '14px' }}>
                        ✓ {assetForm.fileName} ({(assetForm.fileSize / 1024 / 1024).toFixed(2)} MB)
                      </p>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setAssetForm({ ...assetForm, fileUrl: '', fileType: '', fileName: '', fileSize: 0, estimatedDuration: null })}
                        style={{ fontSize: '13px', padding: '6px 12px' }}
                      >
                        Remove File
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => setShowFileUploadModal(true)}
                    >
                      Upload Document
                    </button>
                  )}
                </div>
              )}
              {assetForm.assetType === 'transcript' && (
                <div className="form-group">
                  <label style={{ color: '#1a202c' }}>Transcript File</label>
                  {assetForm.content ? (
                    <div style={{ padding: '12px', background: '#f7fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <p style={{ margin: '0 0 8px 0', color: '#48bb78', fontSize: '14px', fontWeight: '600' }}>
                        ✓ Transcript processed successfully
                      </p>
                      <p style={{ margin: '0 0 12px 0', color: '#4a5568', fontSize: '13px' }}>
                        {assetForm.fileName} ({(assetForm.fileSize / 1024).toFixed(2)} KB)
                      </p>
                      
                      <div style={{ 
                        marginBottom: '12px', 
                        padding: '10px 12px', 
                        background: '#fff3cd', 
                        border: '1px solid #ffc107',
                        borderRadius: '6px'
                      }}>
                        <p style={{ margin: 0, color: '#856404', fontSize: '13px' }}>
                          💡 <strong>The content below is editable.</strong> You can modify the AI-generated transcript before saving.
                        </p>
                      </div>

                      <label style={{ display: 'block', marginBottom: '6px', color: '#1a202c', fontSize: '14px', fontWeight: '600' }}>
                        Edit Transcript Content
                      </label>
                      <textarea
                        value={assetForm.content}
                        onChange={(e) => setAssetForm({ ...assetForm, content: e.target.value })}
                        rows="12"
                        placeholder="Edit the AI-processed transcript content here..."
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '2px solid #667eea',
                          borderRadius: '6px',
                          fontSize: '13px',
                          lineHeight: '1.6',
                          marginBottom: '12px',
                          background: '#ffffff',
                          color: '#1a202c',
                          fontFamily: 'inherit',
                          resize: 'vertical'
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setAssetForm({ ...assetForm, content: '', fileUrl: '', fileType: '', fileName: '', fileSize: 0, filePublicId: '' })}
                        style={{ fontSize: '13px', padding: '6px 12px' }}
                      >
                        Remove & Upload New
                      </button>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept=".txt,.vtt,.srt"
                        onChange={handleTranscriptUpload}
                        style={{ display: 'none' }}
                        id="transcript-upload"
                        disabled={processingTranscript}
                      />
                      <label
                        htmlFor="transcript-upload"
                        className="btn btn-primary"
                        style={{ 
                          cursor: processingTranscript ? 'not-allowed' : 'pointer',
                          opacity: processingTranscript ? 0.6 : 1,
                          display: 'inline-block'
                        }}
                      >
                        {processingTranscript ? '🔄 Processing with AI...' : '📤 Upload Transcript (.txt, .vtt, .srt)'}
                      </label>
                      <small style={{ display: 'block', marginTop: '8px', color: '#718096', fontSize: '12px' }}>
                        Upload a transcript file. AI will automatically clean, format, and summarize the content for students.
                      </small>
                    </div>
                  )}
                </div>
              )}
              {assetForm.assetType === 'markdown' && (
                <div className="form-group">
                  <label style={{ color: '#1a202c' }}>PDF Document</label>
                  {assetForm.content ? (
                    <div style={{ padding: '12px', background: '#f7fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <p style={{ margin: '0 0 8px 0', color: '#48bb78', fontSize: '14px', fontWeight: '600' }}>
                        ✓ PDF processed to Markdown successfully
                      </p>
                      <p style={{ margin: '0 0 12px 0', color: '#4a5568', fontSize: '13px' }}>
                        {assetForm.fileName} ({(assetForm.fileSize / 1024 / 1024).toFixed(2)} MB)
                      </p>
                      
                      <div style={{ 
                        marginBottom: '12px', 
                        padding: '10px 12px', 
                        background: '#fff3cd', 
                        border: '1px solid #ffc107',
                        borderRadius: '6px'
                      }}>
                        <p style={{ margin: 0, color: '#856404', fontSize: '13px' }}>
                          💡 <strong>The markdown content below is editable.</strong> You can modify the AI-generated markdown before saving.
                        </p>
                      </div>

                      <label style={{ display: 'block', marginBottom: '6px', color: '#1a202c', fontSize: '14px', fontWeight: '600' }}>
                        Edit Markdown Content
                      </label>
                      <RichTextEditor
                        value={assetForm.content}
                        onChange={(newContent) => setAssetForm({ ...assetForm, content: newContent })}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setAssetForm({ ...assetForm, content: '', fileUrl: '', fileType: '', fileName: '', fileSize: 0, filePublicId: '' })}
                        style={{ fontSize: '13px', padding: '6px 12px', marginTop: '12px' }}
                      >
                        Remove & Upload New PDF
                      </button>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleMarkdownPDFUpload}
                        style={{ display: 'none' }}
                        id="markdown-pdf-upload"
                        disabled={processingTranscript}
                      />
                      <label
                        htmlFor="markdown-pdf-upload"
                        className="btn btn-primary"
                        style={{ 
                          cursor: processingTranscript ? 'not-allowed' : 'pointer',
                          opacity: processingTranscript ? 0.6 : 1,
                          display: 'inline-block'
                        }}
                      >
                        {processingTranscript ? '🔄 Processing PDF with AI...' : '📤 Upload PDF Document'}
                      </label>
                      <small style={{ display: 'block', marginTop: '8px', color: '#718096', fontSize: '12px' }}>
                        Upload a PDF file. AI will extract and convert the content to well-formatted markdown for students.
                      </small>
                    </div>
                  )}
                </div>
              )}
              {assetForm.assetType === 'quiz' && (
                <div className="form-group">
                  <label style={{ color: '#1a202c' }}>Select Quiz *</label>
                  <Select
                    required
                    value={
                      assetForm.contentReference.refId
                        ? (() => {
                            const quiz = availableQuizzes.find(q => q.id === assetForm.contentReference.refId);
                            return quiz ? {
                              value: quiz.id,
                              label: `${quiz.title} (${quiz.questionsCount || 0} questions)`
                            } : null;
                          })()
                        : null
                    }
                    onChange={(selectedOption) => {
                      setAssetForm({ 
                        ...assetForm, 
                        contentReference: { 
                          refModel: 'Quiz', 
                          refId: selectedOption ? selectedOption.value : '' 
                        }
                      });
                    }}
                    options={availableQuizzes.map(quiz => ({
                      value: quiz.id,
                      label: `${quiz.title} (${quiz.questionsCount || 0} questions)`
                    }))}
                    placeholder="-- Choose a quiz --"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={{
                      control: (base) => ({
                        ...base,
                        background: '#ffffff',
                        borderColor: '#e2e8f0',
                        '&:hover': {
                          borderColor: '#cbd5e0'
                        }
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999
                      })
                    }}
                  />
                </div>
              )}
              {assetForm.assetType === 'flashcard' && (
                <div className="form-group">
                  <label style={{ color: '#1a202c' }}>Select Flashcard Set *</label>
                  <Select
                    required
                    value={
                      assetForm.contentReference.refId
                        ? (() => {
                            const flashcard = availableFlashcards.find(fc => fc._id === assetForm.contentReference.refId);
                            return flashcard ? {
                              value: flashcard._id,
                              label: `${flashcard.title} (${flashcard.termCount || 0} terms)`
                            } : null;
                          })()
                        : null
                    }
                    onChange={(selectedOption) => {
                      setAssetForm({ 
                        ...assetForm, 
                        contentReference: { 
                          refModel: 'FlashCard', 
                          refId: selectedOption ? selectedOption.value : '' 
                        }
                      });
                    }}
                    options={availableFlashcards.map(flashcard => ({
                      value: flashcard._id,
                      label: `${flashcard.title} (${flashcard.termCount || 0} terms)`
                    }))}
                    placeholder="-- Choose a flashcard set --"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={{
                      control: (base) => ({
                        ...base,
                        background: '#ffffff',
                        borderColor: '#e2e8f0',
                        '&:hover': {
                          borderColor: '#cbd5e0'
                        }
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999
                      })
                    }}
                  />
                </div>
              )}
              {assetForm.assetType === 'interview' && (
                <div className="form-group">
                  <label style={{ color: '#1a202c' }}>Select Interview *</label>
                  <Select
                    required
                    value={
                      assetForm.contentReference.refId
                        ? (() => {
                            const interview = availableInterviews.find(i => i._id === assetForm.contentReference.refId);
                            return interview ? {
                              value: interview._id,
                              label: interview.title
                            } : null;
                          })()
                        : null
                    }
                    onChange={(selectedOption) => {
                      setAssetForm({ 
                        ...assetForm, 
                        contentReference: { 
                          refModel: 'Interview', 
                          refId: selectedOption ? selectedOption.value : '' 
                        }
                      });
                    }}
                    options={availableInterviews.map(interview => ({
                      value: interview._id,
                      label: interview.title
                    }))}
                    placeholder="-- Choose an interview --"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={{
                      control: (base) => ({
                        ...base,
                        background: '#ffffff',
                        borderColor: '#e2e8f0',
                        '&:hover': {
                          borderColor: '#cbd5e0'
                        }
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999
                      })
                    }}
                  />
                </div>
              )}
              <div className="form-group">
                <label style={{ color: '#1a202c', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={assetForm.isRequired}
                    onChange={(e) => setAssetForm({ ...assetForm, isRequired: e.target.checked })}
                  />
                  Required for course completion
                </label>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleAssetModalClose}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={processingTranscript}
                  style={{
                    cursor: processingTranscript ? 'not-allowed' : 'pointer',
                    opacity: processingTranscript ? 0.6 : 1
                  }}
                >
                  {processingTranscript ? '🔄 Processing...' : (editingAsset ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
            )}

            {/* Import Asset Tab Content */}
            {assetModalTab === 'import' && (
              <div>
                {/* Search and Filter */}
                <div style={{ display: 'grid', gap: '12px', marginBottom: '20px' }}>
                  <input
                    type="text"
                    placeholder="Search assets by title..."
                    value={importSearchTerm}
                    onChange={(e) => setImportSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        fetchImportAssets();
                      }
                    }}
                    style={{
                      padding: '10px 12px',
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      color: '#2d3748',
                      fontSize: '14px'
                    }}
                  />
                  <div style={{
                    display: 'grid',
                    gap: '12px',
                    gridTemplateColumns: '1fr 1fr'
                  }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', color: '#4a5568', marginBottom: '6px', fontWeight: '600' }}>
                        Source Course
                      </label>
                      <select
                        value={importCourseFilter}
                        onChange={(e) => {
                          setImportCourseFilter(e.target.value);
                          setImportChapterFilter('');
                        }}
                        disabled={loadingImportCourses}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          background: '#ffffff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          color: '#2d3748',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">All Courses</option>
                        {importCourseOptions.map(courseOption => (
                          <option key={courseOption._id} value={courseOption._id}>
                            {courseOption.title}{courseOption.code ? ` (${courseOption.code})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', color: '#4a5568', marginBottom: '6px', fontWeight: '600' }}>
                        Source Chapter
                      </label>
                      <select
                        value={importChapterFilter}
                        onChange={(e) => setImportChapterFilter(e.target.value)}
                        disabled={!importCourseFilter || loadingImportChapters}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          background: '#ffffff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          color: importCourseFilter ? '#2d3748' : '#a0aec0',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">
                          {!importCourseFilter
                            ? 'Select a course first'
                            : loadingImportChapters
                              ? 'Loading chapters...'
                              : 'All Chapters'}
                        </option>
                        {importChapterOptions.map(chapterOption => (
                          <option key={chapterOption._id} value={chapterOption._id}>
                            {chapterOption.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <select
                    value={importTypeFilter}
                    onChange={(e) => setImportTypeFilter(e.target.value)}
                    style={{
                      padding: '10px 12px',
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      color: '#2d3748',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">All Types</option>
                    <option value="note">📋 Note</option>
                    <option value="link">🔗 Link</option>
                    <option value="video">🎥 Video</option>
                    <option value="document">📄 Document</option>
                    <option value="markdown">📝 Markdown</option>
                    <option value="transcript">📄 Transcript</option>
                    <option value="quiz">📝 Quiz</option>
                    <option value="flashcard">🎴 Flashcard</option>
                    <option value="interview">🎤 Interview</option>
                  </select>
                </div>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={fetchImportAssets}
                  style={{ width: '100%', marginBottom: '16px' }}
                  disabled={loadingImportAssets}
                >
                  {loadingImportAssets ? '🔄 Loading...' : '🔍 Search Assets'}
                </button>

                {!loadingImportAssets && importAssets.length > 0 && (
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px',
                    color: '#2d3748',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={importAssets.length > 0 && selectedAssetsToImport.length === importAssets.length}
                      onChange={(e) => handleToggleSelectAllImportAssets(e.target.checked)}
                    />
                    Select All ({importAssets.length})
                  </label>
                )}

                {/* Assets List */}
                <div style={{ 
                  maxHeight: '400px', 
                  overflowY: 'auto',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  {loadingImportAssets ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
                      <p>Loading assets...</p>
                    </div>
                  ) : importAssets.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>📦</div>
                      <p>No assets found</p>
                      <small style={{ fontSize: '13px' }}>Click "Search Assets" to load available assets</small>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {importAssets.map(asset => {
                        const isSelected = selectedAssetsToImport.some(selected => selected._id === asset._id);
                        return (
                        <div
                          key={asset._id}
                          onClick={() => handleToggleAssetImportSelection(asset)}
                          style={{
                            padding: '12px',
                            background: isSelected ? '#edf2f7' : '#f7fafc',
                            borderRadius: '8px',
                            border: isSelected ? '1px solid #667eea' : '1px solid #e2e8f0',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#667eea';
                            e.currentTarget.style.background = '#edf2f7';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#e2e8f0';
                            e.currentTarget.style.background = '#f7fafc';
                          }}
                        >
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'start',
                            marginBottom: '6px'
                          }}>
                            <div style={{ marginRight: '10px', marginTop: '2px' }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                readOnly
                                style={{ cursor: 'pointer' }}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ 
                                fontWeight: '600', 
                                color: '#1a202c', 
                                fontSize: '14px',
                                marginBottom: '4px'
                              }}>
                                {asset.title}
                              </div>
                              <div style={{ 
                                fontSize: '12px', 
                                color: '#718096',
                                textTransform: 'capitalize'
                              }}>
                                {asset.assetType}
                              </div>
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#718096',
                              textAlign: 'right'
                            }}>
                              <div>{asset.course?.title || 'Unknown course'}</div>
                              {asset.chapter?.title && (
                                <div style={{ fontSize: '11px', marginTop: '2px' }}>
                                  📖 {asset.chapter.title}
                                </div>
                              )}
                              <div style={{ fontSize: '11px', marginTop: '2px' }}>
                                {new Date(asset.createdAt).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                          {asset.description && (
                            <div style={{ 
                              fontSize: '12px', 
                              color: '#4a5568',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {asset.description}
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setSelectedAssetsToImport([])}
                    disabled={selectedAssetsToImport.length === 0}
                  >
                    Clear Selection
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={selectedAssetsToImport.length === 0}
                    onClick={() => {
                      setShowAssetModal(false);
                      setShowImportModeModal(true);
                    }}
                  >
                    Import Selected ({selectedAssetsToImport.length})
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={handleAssetModalClose}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            </>
          ) : (
            <>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#1a202c' }}>
                Asset {editingAsset ? 'Updated' : 'Created'} Successfully!
              </h2>
              <div style={{ 
                padding: '16px', 
                marginBottom: '20px', 
                background: '#f0fdf4', 
                border: '1px solid #86efac', 
                borderRadius: '8px' 
              }}>
                <div style={{ fontWeight: '600', color: '#166534', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>
                    {createdAsset.assetType === 'note' && '📋'}
                    {createdAsset.assetType === 'link' && '🔗'}
                    {createdAsset.assetType === 'video' && '▶️'}
                    {createdAsset.assetType === 'document' && '📄'}
                    {createdAsset.assetType === 'markdown' && '📝'}
                    {createdAsset.assetType === 'transcript' && '📄'}
                    {createdAsset.assetType === 'quiz' && '📝'}
                    {createdAsset.assetType === 'flashcard' && '🎴'}
                    {createdAsset.assetType === 'interview' && '🎤'}
                  </span>
                  {createdAsset.title}
                </div>
                {createdAsset.description && (
                  <div style={{ fontSize: '13px', color: '#4b5563' }}>
                    {createdAsset.description}
                  </div>
                )}
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  Type: {createdAsset.assetType.charAt(0).toUpperCase() + createdAsset.assetType.slice(1)}
                </div>
              </div>
              
              <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#4b5563' }}>
                What would you like to do next?
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button 
                  onClick={handleAssetModalEdit}
                  className="btn btn-secondary"
                  style={{ 
                    width: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <span>✏️</span>
                  <span>Edit Asset</span>
                </button>
                
                <button 
                  onClick={handleAssetModalAddAnother}
                  className="btn btn-primary"
                  style={{ 
                    width: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <span>➕</span>
                  <span>Add Another Asset</span>
                </button>
                
                <button 
                  onClick={handleAssetModalDelete}
                  className="btn"
                  style={{ 
                    width: '100%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '8px',
                    background: '#fee2e2',
                    color: '#991b1b',
                    border: '1px solid #fecaca'
                  }}
                >
                  <span>🗑️</span>
                  <span>Delete Asset</span>
                </button>

                <button 
                  onClick={handleAssetModalClose}
                  className="btn btn-secondary"
                  style={{ 
                    width: '100%',
                    marginTop: '10px'
                  }}
                >
                  Done
                </button>
              </div>
            </>
          )}
        </Box>
      </Modal>

      {/* Import Mode Selection Modal */}
      <Modal
        open={showImportModeModal && selectedAssetsToImport.length > 0}
        onClose={() => setShowImportModeModal(false)}
        aria-labelledby="import-mode-title"
        aria-describedby="import-mode-description"
        disableScrollLock={true}
        BackdropProps={{
          timeout: 0
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '500px',
            bgcolor: '#ffffff',
            borderRadius: '12px',
            p: '24px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            outline: 'none'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h3
            id="import-mode-title"
            style={{
              margin: '0 0 16px 0',
              fontSize: '18px',
              color: '#1a202c',
              fontWeight: '700'
            }}
          >
            {selectedAssetsToImport.length === 1
              ? `Import "${selectedAssetsToImport[0].title}"`
              : `Import ${selectedAssetsToImport.length} Assets`
            }
          </h3>

          <p
            id="import-mode-description"
            style={{ color: '#718096', fontSize: '14px', marginBottom: '24px' }}
          >
            How would you like to import{' '}
            {selectedAssetsToImport.length === 1 ? 'this asset' : 'these assets'}?
          </p>

          <div style={{ display: 'grid', gap: '12px', marginBottom: '24px' }}>
            {/* Link Option */}
            <div
              onClick={() => handleImportAssets(true)}
              style={{
                padding: '16px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#667eea';
                e.currentTarget.style.background = '#f7fafc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.background = '#ffffff';
              }}
            >
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                🔗
              </div>
              <div
                style={{
                  fontWeight: '600',
                  color: '#1a202c',
                  fontSize: '15px',
                  marginBottom: '6px'
                }}
              >
                Link to Original
              </div>
              <div
                style={{
                  fontSize: '13px',
                  color: '#718096',
                  lineHeight: '1.5'
                }}
              >
                Any changes to the original asset will automatically reflect in this course. Perfect for shared content that should stay synchronized.
              </div>
            </div>

            {/* Copy Option */}
            <div
              onClick={() => handleImportAssets(false)}
              style={{
                padding: '16px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#667eea';
                e.currentTarget.style.background = '#f7fafc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.background = '#ffffff';
              }}
            >
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                📋
              </div>
              <div
                style={{
                  fontWeight: '600',
                  color: '#1a202c',
                  fontSize: '15px',
                  marginBottom: '6px'
                }}
              >
                Create Independent Copy
              </div>
              <div
                style={{
                  fontSize: '13px',
                  color: '#718096',
                  lineHeight: '1.5'
                }}
              >
                Create a separate copy that you can modify independently. Changes to the original won't affect this copy.
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowImportModeModal(false)}
            className="btn btn-secondary"
            style={{ width: '100%' }}
          >
            Cancel
          </button>
        </Box>
      </Modal>

      {/* File Upload Modal */}
      {showFileUploadModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}
          onClick={() => setShowFileUploadModal(false)}>
          <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '500px' }}
            onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#1a202c' }}>
              Upload {assetForm.assetType === 'video' ? 'Video' : 'Document'}
            </h2>
            <FileUploader
              acceptedTypes={assetForm.assetType === 'video' ? 'video/*' : '.pdf,.doc,.docx,.ppt,.pptx'}
              maxSize={500 * 1024 * 1024}
              onUploadComplete={handleFileUpload}
            />
            <button
              className="btn btn-secondary"
              onClick={() => setShowFileUploadModal(false)}
              style={{ marginTop: '16px', width: '100%' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* View Asset Modal */}
      <Modal
        open={!!viewingAsset}
        onClose={() => {
          setViewingAsset(null);
          setEditingTranscriptTitle(false);
          setTranscriptTitleValue('');
        }}
        aria-labelledby="asset-modal-title"
        aria-describedby="asset-modal-description"
        disableScrollLock={true}
        BackdropProps={{
          timeout: 0
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '5%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: '95%', sm: '90%', md: '900px' },
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: '12px',
            boxShadow: 24,
            p: 3,
            overflowY: 'auto',
            outline: 'none'
          }}
        >
          {viewingAsset && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '20px' }}>
                <div style={{ flex: 1, marginRight: '16px' }}>
                  {viewingAsset.assetType === 'transcript' && editingTranscriptTitle ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <input
                        type="text"
                        value={transcriptTitleValue}
                        onChange={(e) => setTranscriptTitleValue(e.target.value)}
                        style={{
                          fontSize: '22px',
                          fontWeight: '600',
                          color: '#1a202c',
                          border: '2px solid #667eea',
                          borderRadius: '6px',
                          padding: '8px 12px',
                          outline: 'none',
                          width: '100%'
                        }}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveTranscriptTitle();
                          } else if (e.key === 'Escape') {
                            setEditingTranscriptTitle(false);
                            setTranscriptTitleValue(viewingAsset.title);
                          }
                        }}
                      />
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={handleSaveTranscriptTitle}
                          style={{
                            padding: '6px 16px',
                            background: '#667eea',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500'
                          }}
                        >
                          ✓ Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingTranscriptTitle(false);
                            setTranscriptTitleValue(viewingAsset.title);
                          }}
                          style={{
                            padding: '6px 16px',
                            background: '#e2e8f0',
                            color: '#1a202c',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500'
                          }}
                        >
                          ✕ Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <h2 id="asset-modal-title" style={{ margin: '0 0 8px 0', fontSize: '22px', color: '#1a202c' }}>
                          {viewingAsset.assetType === 'video' && '▶️ '}
                          {viewingAsset.assetType === 'document' && '📄 '}
                          {viewingAsset.assetType === 'quiz' && '📝 '}
                          {viewingAsset.assetType === 'flashcard' && '🎴 '}
                          {viewingAsset.assetType === 'interview' && '🎤 '}
                          {viewingAsset.assetType === 'link' && '🔗 '}
                          {viewingAsset.assetType === 'note' && '📋 '}
                          {viewingAsset.assetType === 'transcript' && '📝 '}
                          {viewingAsset.title}
                        </h2>
                        {viewingAsset.assetType === 'transcript' && (
                          <button
                            onClick={() => {
                              setEditingTranscriptTitle(true);
                              setTranscriptTitleValue(viewingAsset.title);
                            }}
                            style={{
                              padding: '4px 10px',
                              background: '#f7fafc',
                              color: '#667eea',
                              border: '1px solid #e2e8f0',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              marginBottom: '8px'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#edf2f7';
                              e.currentTarget.style.borderColor = '#667eea';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#f7fafc';
                              e.currentTarget.style.borderColor = '#e2e8f0';
                            }}
                          >
                            ✏️ Edit Title
                          </button>
                        )}
                      </div>
                      <p style={{ margin: 0, fontSize: '13px', color: '#718096' }}>
                        Type: {viewingAsset.assetType}
                        {viewingAsset.chapter && ` • Chapter: ${viewingAsset.chapter.title}`}
                        {viewingAsset.isRequired && <span style={{ marginLeft: '8px', padding: '2px 8px', background: '#f56565', color: '#fff', borderRadius: '4px', fontSize: '11px' }}>Required</span>}
                      </p>
                    </>
                  )}
                </div>
                <IconButton
                  aria-label="close"
                  onClick={() => {
                    setViewingAsset(null);
                    setEditingTranscriptTitle(false);
                    setTranscriptTitleValue('');
                  }}
                  sx={{
                    color: '#718096',
                    '&:hover': {
                      backgroundColor: '#f7fafc',
                      color: '#667eea'
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </div>

            {viewingAsset.description && (
              <div style={{ marginBottom: '20px', padding: '12px', background: '#f7fafc', borderRadius: '8px' }}>
                <p style={{ margin: 0, color: '#4a5568', fontSize: '14px' }}>{viewingAsset.description}</p>
              </div>
            )}

            {/* Study Questions Status and Generate Button */}
            {(viewingAsset.assetType === 'note' || viewingAsset.assetType === 'markdown' || viewingAsset.assetType === 'transcript') && viewingAsset.content && viewingAsset.content.trim().length >= 100 && (
              <div style={{ 
                marginBottom: '20px', 
                padding: '16px', 
                background: viewingAsset.studyQuestions && viewingAsset.studyQuestions.length > 0 ? '#f0fdf4' : '#fff7ed',
                borderRadius: '8px',
                border: `1px solid ${viewingAsset.studyQuestions && viewingAsset.studyQuestions.length > 0 ? '#86efac' : '#fed7aa'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '20px' }}>
                        {viewingAsset.studyQuestions && viewingAsset.studyQuestions.length > 0 ? '✓' : '💡'}
                      </span>
                      <strong style={{ color: viewingAsset.studyQuestions && viewingAsset.studyQuestions.length > 0 ? '#166534' : '#9a3412', fontSize: '14px' }}>
                        {viewingAsset.studyQuestions && viewingAsset.studyQuestions.length > 0 
                          ? `${viewingAsset.studyQuestions.length} Study Questions Available` 
                          : 'No Study Questions Yet'}
                      </strong>
                    </div>
                    <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                      {viewingAsset.studyQuestions && viewingAsset.studyQuestions.length > 0
                        ? 'AI-generated questions to help students understand this content'
                        : 'Generate AI-powered questions to help students understand this content'}
                    </p>
                  </div>
                  <button
                    onClick={() => handleGenerateStudyQuestions(viewingAsset._id)}
                    disabled={generatingQuestions}
                    style={{
                      padding: '10px 16px',
                      background: generatingQuestions ? '#d1d5db' : '#667eea',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: generatingQuestions ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {generatingQuestions ? (
                      <>
                        <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
                        Generating...
                      </>
                    ) : (
                      <>
                        <span>✨</span>
                        {viewingAsset.studyQuestions && viewingAsset.studyQuestions.length > 0 ? 'Regenerate' : 'Generate'} Questions
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Quiz Generation Section */}
            {(viewingAsset.assetType === 'note' || viewingAsset.assetType === 'markdown' || viewingAsset.assetType === 'transcript') && viewingAsset.content && viewingAsset.content.trim().length >= 100 && (
              <div style={{ 
                marginBottom: '20px', 
                padding: '16px', 
                background: viewingAsset._id.startsWith('temp_') ? '#fef2f2' : (assetQuizzes[viewingAsset._id] ? '#f0f9ff' : '#fefce8'),
                borderRadius: '8px',
                border: `1px solid ${viewingAsset._id.startsWith('temp_') ? '#fecaca' : (assetQuizzes[viewingAsset._id] ? '#7dd3fc' : '#fde047')}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '20px' }}>
                        {assetQuizzes[viewingAsset._id] ? '📝' : '💭'}
                      </span>
                      <strong style={{ color: assetQuizzes[viewingAsset._id] ? '#0c4a6e' : '#713f12', fontSize: '14px' }}>
                        {assetQuizzes[viewingAsset._id]
                          ? `Quiz Available (${assetQuizzes[viewingAsset._id].questions.length} questions)` 
                          : 'No Quiz Yet'}
                      </strong>
                    </div>
                    <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                      {viewingAsset._id.startsWith('temp_')
                        ? '⚠️ Save the course first to enable quiz generation for this asset'
                        : (assetQuizzes[viewingAsset._id]
                          ? 'Students can take this quiz up to 3 times after reading the content'
                          : 'Generate an AI-powered quiz to test student understanding')}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {assetQuizzes[viewingAsset._id] && (
                      <button
                        onClick={() => handleDeleteQuiz(viewingAsset._id)}
                        style={{
                          padding: '10px 16px',
                          background: '#ef4444',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <span>🗑️</span>
                        Delete Quiz
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (viewingAsset._id.startsWith('temp_')) {
                          alert('Please save the course changes first before generating a quiz for this asset.');
                        } else {
                          handleOpenQuizGeneration(viewingAsset._id);
                        }
                      }}
                      disabled={generatingQuiz || viewingAsset._id.startsWith('temp_')}
                      style={{
                        padding: '10px 16px',
                        background: generatingQuiz || viewingAsset._id.startsWith('temp_') ? '#d1d5db' : '#3b82f6',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: generatingQuiz || viewingAsset._id.startsWith('temp_') ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      title={viewingAsset._id.startsWith('temp_') ? 'Save changes first to enable quiz generation' : ''}
                    >
                      {generatingQuiz ? (
                        <>
                          <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
                          Generating...
                        </>
                      ) : (
                        <>
                          <span>🎯</span>
                          {assetQuizzes[viewingAsset._id] ? 'Regenerate' : 'Generate'} Quiz
                          {viewingAsset._id.startsWith('temp_') && ' (Save First)'}
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Quiz Questions List */}
                {assetQuizzes[viewingAsset._id] && (
                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #cbd5e1' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <strong style={{ fontSize: '13px', color: '#475569' }}>Questions:</strong>
                      <button
                        onClick={handleOpenAddQuizQuestion}
                        style={{
                          padding: '6px 12px',
                          background: '#10b981',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <span>➕</span>
                        Add Question
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {assetQuizzes[viewingAsset._id].questions.map((q, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: '12px',
                            background: '#ffffff',
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'start',
                            gap: '12px'
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <span style={{ 
                                fontSize: '11px', 
                                fontWeight: '700', 
                                color: '#6b7280',
                                background: '#f3f4f6',
                                padding: '2px 8px',
                                borderRadius: '4px'
                              }}>
                                Q{idx + 1}
                              </span>
                              <span style={{ 
                                fontSize: '10px', 
                                fontWeight: '600', 
                                color: q.questionType === 'MCQ' ? '#3b82f6' : '#8b5cf6',
                                background: q.questionType === 'MCQ' ? '#eff6ff' : '#f5f3ff',
                                padding: '2px 6px',
                                borderRadius: '3px'
                              }}>
                                {q.questionType === 'MCQ' ? 'Multiple Choice' : 'True/False'}
                              </span>
                              <span style={{ 
                                fontSize: '10px', 
                                fontWeight: '600', 
                                color: q.difficulty === 'Easy' ? '#10b981' : q.difficulty === 'Medium' ? '#f59e0b' : '#ef4444',
                                background: q.difficulty === 'Easy' ? '#d1fae5' : q.difficulty === 'Medium' ? '#fef3c7' : '#fee2e2',
                                padding: '2px 6px',
                                borderRadius: '3px'
                              }}>
                                {q.difficulty}
                              </span>
                            </div>
                            <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#1f2937', fontWeight: '500', lineHeight: '1.4' }}>
                              {q.question}
                            </p>
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>
                              {q.options.map((opt, optIdx) => (
                                <div key={optIdx} style={{ 
                                  marginBottom: '2px',
                                  fontWeight: optIdx === q.correctAnswer ? '600' : 'normal',
                                  color: optIdx === q.correctAnswer ? '#10b981' : '#6b7280'
                                }}>
                                  {optIdx === q.correctAnswer && '✓ '}{String.fromCharCode(65 + optIdx)}. {opt}
                                </div>
                              ))}
                            </div>
                            {q.explanation && (
                              <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: '#6b7280', fontStyle: 'italic' }}>
                                <strong>Explanation:</strong> {q.explanation}
                              </p>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                            <button
                              onClick={() => handleOpenEditQuizQuestion(idx)}
                              style={{
                                padding: '6px 10px',
                                background: '#f3f4f6',
                                color: '#374151',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                fontWeight: '600'
                              }}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => handleDeleteQuizQuestion(idx)}
                              style={{
                                padding: '6px 10px',
                                background: '#fee2e2',
                                color: '#991b1b',
                                border: '1px solid #fecaca',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                fontWeight: '600'
                              }}
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Assignment Generation Section for Transcript/Note/Markdown Assets */}
            {(viewingAsset.assetType === 'transcript' || viewingAsset.assetType === 'note' || viewingAsset.assetType === 'markdown') && (
              <div style={{ marginTop: '20px', padding: '16px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <strong style={{ fontSize: '14px', color: '#374151' }}>📝 Assignment Generation</strong>
                      {generatedAssignments.length > 0 && (
                        <span style={{ fontSize: '11px', fontWeight: '600', color: '#10b981', background: '#d1fae5', padding: '2px 8px', borderRadius: '10px' }}>
                          {generatedAssignments.length} Generated
                        </span>
                      )}
                    </div>
                    <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                      {viewingAsset._id.startsWith('temp_')
                        ? '⚠️ Save the course first to enable assignment generation for this asset'
                        : (generatedAssignments.length > 0
                          ? 'Review generated assignments and select which ones to save'
                          : 'Generate AI-powered coding assignments from this content')}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (viewingAsset._id.startsWith('temp_')) {
                        alert('Please save the course changes first before generating assignments for this asset.');
                      } else {
                        handleOpenGenerateAssignments();
                      }
                    }}
                    disabled={generatingAssignments || viewingAsset._id.startsWith('temp_')}
                    style={{
                      padding: '10px 16px',
                      background: generatingAssignments || viewingAsset._id.startsWith('temp_') ? '#d1d5db' : '#8b5cf6',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: generatingAssignments || viewingAsset._id.startsWith('temp_') ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    title={viewingAsset._id.startsWith('temp_') ? 'Save changes first to enable assignment generation' : ''}
                  >
                    {generatingAssignments ? (
                      <>
                        <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
                        Generating...
                      </>
                    ) : (
                      <>
                        <span>📝</span>
                        Generate Assignments
                        {viewingAsset._id.startsWith('temp_') && ' (Save First)'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            <div style={{ marginTop: '20px' }}>
              {viewingAsset.assetType === 'note' && viewingAsset.content && (
                <div className="markdown-content" style={{ padding: '16px', background: '#ffffff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || '');
                        return !inline && match ? (
                          <SyntaxHighlighter
                            style={vscDarkPlus}
                            language={match[1]}
                            PreTag="div"
                            {...props}
                          >
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        ) : (
                          <code className={className} {...props} style={{ background: '#f7fafc', padding: '2px 6px', borderRadius: '4px', fontSize: '0.9em' }}>
                            {children}
                          </code>
                        );
                      }
                    }}
                  >
                    {viewingAsset.content}
                  </ReactMarkdown>
                </div>
              )}

              {viewingAsset.assetType === 'transcript' && viewingAsset.content && (
                <div className="markdown-content" style={{ padding: '16px', background: '#ffffff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ marginBottom: '12px', padding: '10px', background: '#e6f7ff', borderLeft: '3px solid #1890ff', borderRadius: '4px' }}>
                    <p style={{ margin: 0, fontSize: '13px', color: '#0050b3' }}>
                      📝 <strong>AI-Processed Transcript:</strong> This content has been automatically cleaned, formatted, and summarized from the uploaded transcript.
                    </p>
                  </div>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      table: ({node, ...props}) => (
                        <table style={{ 
                          borderCollapse: 'collapse',
                          width: '100%',
                          marginBottom: '16px',
                          background: '#ffffff',
                          color: '#1a202c'
                        }} {...props} />
                      ),
                      th: ({node, ...props}) => (
                        <th style={{ 
                          border: '1px solid #e2e8f0',
                          padding: '12px 8px',
                          background: '#f7fafc',
                          color: '#1a202c',
                          fontWeight: '600',
                          textAlign: 'left'
                        }} {...props} />
                      ),
                      td: ({node, ...props}) => (
                        <td style={{ 
                          border: '1px solid #e2e8f0',
                          padding: '10px 8px',
                          background: '#ffffff',
                          color: '#1a202c'
                        }} {...props} />
                      ),
                      code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || '');
                        return !inline && match ? (
                          <SyntaxHighlighter
                            style={vscDarkPlus}
                            language={match[1]}
                            PreTag="div"
                            {...props}
                          >
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        ) : (
                          <code className={className} {...props} style={{ background: '#f7fafc', padding: '2px 6px', borderRadius: '4px', fontSize: '0.9em' }}>
                            {children}
                          </code>
                        );
                      }
                    }}
                  >
                    {viewingAsset.content}
                  </ReactMarkdown>
                  {viewingAsset.fileName && (
                    <div style={{ marginTop: '16px', padding: '10px', background: '#f7fafc', borderRadius: '4px', fontSize: '12px', color: '#718096' }}>
                      📄 Original file: {viewingAsset.fileName}
                    </div>
                  )}
                </div>
              )}

              {viewingAsset.assetType === 'markdown' && viewingAsset.content && (
                <div className="markdown-content" style={{ padding: '16px', background: '#ffffff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ marginBottom: '12px', padding: '10px', background: '#f0f9ff', borderLeft: '3px solid #667eea', borderRadius: '4px' }}>
                    <p style={{ margin: 0, fontSize: '13px', color: '#1e40af' }}>
                      📝 <strong>AI-Processed PDF Content:</strong> This content has been automatically extracted and converted to markdown from the uploaded PDF.
                    </p>
                  </div>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      table: ({node, ...props}) => (
                        <table style={{ 
                          borderCollapse: 'collapse',
                          width: '100%',
                          marginBottom: '16px',
                          background: '#ffffff',
                          color: '#1a202c'
                        }} {...props} />
                      ),
                      th: ({node, ...props}) => (
                        <th style={{ 
                          border: '1px solid #e2e8f0',
                          padding: '12px 8px',
                          background: '#f7fafc',
                          color: '#1a202c',
                          fontWeight: '600',
                          textAlign: 'left'
                        }} {...props} />
                      ),
                      td: ({node, ...props}) => (
                        <td style={{ 
                          border: '1px solid #e2e8f0',
                          padding: '10px 8px',
                          background: '#ffffff',
                          color: '#1a202c'
                        }} {...props} />
                      ),
                      code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || '');
                        return !inline && match ? (
                          <SyntaxHighlighter
                            style={vscDarkPlus}
                            language={match[1]}
                            PreTag="div"
                            {...props}
                          >
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        ) : (
                          <code className={className} {...props} style={{ background: '#f7fafc', padding: '2px 6px', borderRadius: '4px', fontSize: '0.9em' }}>
                            {children}
                          </code>
                        );
                      }
                    }}
                  >
                    {viewingAsset.content}
                  </ReactMarkdown>
                  {viewingAsset.fileName && (
                    <div style={{ marginTop: '16px', padding: '10px', background: '#f7fafc', borderRadius: '4px', fontSize: '12px', color: '#718096' }}>
                      📄 Original PDF: {viewingAsset.fileName}
                    </div>
                  )}
                </div>
              )}

              {viewingAsset.assetType === 'link' && viewingAsset.externalUrl && (
                <div style={{ padding: '16px', background: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <p style={{ margin: '0 0 12px 0', color: '#4a5568', fontSize: '14px' }}>External Resource:</p>
                  <a 
                    href={viewingAsset.externalUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      display: 'inline-block',
                      padding: '10px 16px',
                      background: '#667eea',
                      color: '#ffffff',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    Open Link →
                  </a>
                  <p style={{ margin: '12px 0 0 0', fontSize: '12px', color: '#718096', wordBreak: 'break-all' }}>
                    {viewingAsset.externalUrl}
                  </p>
                </div>
              )}

              {viewingAsset.assetType === 'video' && (viewingAsset.fileUrl || viewingAsset.externalUrl) && (
                <div style={{ padding: '16px', background: '#000', borderRadius: '8px' }}>
                  {(() => {
                    const embedUrl = viewingAsset.externalUrl ? getVideoEmbedUrl(viewingAsset.externalUrl) : null;
                    
                    if (embedUrl) {
                      // Show embedded YouTube/Vimeo video
                      return (
                        <div style={{ 
                          position: 'relative', 
                          paddingBottom: '56.25%', // 16:9 aspect ratio
                          height: 0,
                          overflow: 'hidden',
                          borderRadius: '8px'
                        }}>
                          <iframe
                            src={embedUrl}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              border: 'none',
                              borderRadius: '8px'
                            }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                            allowFullScreen
                            title="Video player"
                          />
                        </div>
                      );
                    } else if (viewingAsset.fileUrl) {
                      // Show uploaded video
                      return (
                        <>
                          <video 
                            controls 
                            style={{ width: '100%', maxHeight: '500px', borderRadius: '8px' }}
                            src={viewingAsset.fileUrl}
                          >
                            Your browser does not support the video tag.
                          </video>
                          {viewingAsset.fileName && (
                            <p style={{ margin: '12px 0 0 0', color: '#ffffff', fontSize: '13px' }}>
                              📁 {viewingAsset.fileName} ({(viewingAsset.fileSize / 1024 / 1024).toFixed(2)} MB)
                            </p>
                          )}
                        </>
                      );
                    }
                  })()}
                </div>
              )}

              {viewingAsset.assetType === 'document' && viewingAsset.fileUrl && (
                <div style={{ padding: '16px', background: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                  <p style={{ margin: '0 0 12px 0', fontSize: '48px' }}>📄</p>
                  {viewingAsset.fileName && (
                    <p style={{ margin: '0 0 12px 0', color: '#1a202c', fontSize: '16px', fontWeight: '600' }}>
                      {viewingAsset.fileName}
                    </p>
                  )}
                  {viewingAsset.fileSize && (
                    <p style={{ margin: '0 0 16px 0', color: '#718096', fontSize: '13px' }}>
                      Size: {(viewingAsset.fileSize / 1024 / 1024).toFixed(2)} MB
                    </p>
                  )}
                  <a 
                    href={viewingAsset.fileUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      display: 'inline-block',
                      padding: '10px 20px',
                      background: '#667eea',
                      color: '#ffffff',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    Download / View →
                  </a>
                </div>
              )}

              {(viewingAsset.assetType === 'quiz' || viewingAsset.assetType === 'flashcard' || viewingAsset.assetType === 'interview') && (
                <div style={{ padding: '24px', background: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                  <p style={{ margin: '0 0 12px 0', fontSize: '48px' }}>
                    {viewingAsset.assetType === 'quiz' && '📝'}
                    {viewingAsset.assetType === 'flashcard' && '🎴'}
                    {viewingAsset.assetType === 'interview' && '🎤'}
                  </p>
                  <p style={{ margin: '0 0 8px 0', color: '#1a202c', fontSize: '18px', fontWeight: '600' }}>
                    {viewingAsset.assetType === 'quiz' && 'Quiz Activity'}
                    {viewingAsset.assetType === 'flashcard' && 'Flashcard Set'}
                    {viewingAsset.assetType === 'interview' && 'Interview Practice'}
                  </p>
                  <p style={{ margin: 0, color: '#718096', fontSize: '14px' }}>
                    Students will access this activity from the course page
                  </p>
                </div>
              )}
            </div>

            {/* Study Questions Display for Admin */}
            {viewingAsset.studyQuestions && viewingAsset.studyQuestions.length > 0 && (
              <div style={{
                marginTop: '30px',
                padding: '24px',
                background: '#f8fafc',
                borderRadius: '12px',
                border: '2px solid #e2e8f0'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '20px',
                  paddingBottom: '16px',
                  borderBottom: '2px solid #e2e8f0'
                }}>
                  <span style={{ fontSize: '28px' }}>💡</span>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ 
                      margin: 0, 
                      fontSize: '20px', 
                      fontWeight: '600',
                      color: '#1a202c'
                    }}>
                      Study Questions ({viewingAsset.studyQuestions.length})
                    </h3>
                    <p style={{ 
                      margin: '4px 0 0 0', 
                      fontSize: '13px', 
                      color: '#64748b'
                    }}>
                      AI-generated questions to help students understand this content
                    </p>
                  </div>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {viewingAsset.studyQuestions.map((q, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '20px',
                        background: '#ffffff',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        marginBottom: '12px'
                      }}>
                        <div style={{
                          minWidth: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: '#667eea',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}>
                          {index + 1}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#1e293b',
                            marginBottom: '8px',
                            lineHeight: '1.5'
                          }}>
                            {q.question}
                          </div>
                          <div style={{
                            fontSize: '14px',
                            color: '#475569',
                            lineHeight: '1.6',
                            padding: '12px',
                            background: '#f8fafc',
                            borderRadius: '6px',
                            borderLeft: '3px solid #667eea'
                          }}>
                            <strong style={{ color: '#667eea' }}>Answer:</strong>
                            <div style={{ marginTop: '6px' }}>
                              {q.answer}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        justifyContent: 'flex-end',
                        marginTop: '12px'
                      }}>
                        <button
                          onClick={() => handleEditQuestion(viewingAsset._id, index, q.question, q.answer)}
                          style={{
                            padding: '6px 12px',
                            background: '#f1f5f9',
                            color: '#475569',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#667eea';
                            e.currentTarget.style.color = '#ffffff';
                            e.currentTarget.style.borderColor = '#667eea';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#f1f5f9';
                            e.currentTarget.style.color = '#475569';
                            e.currentTarget.style.borderColor = '#cbd5e1';
                          }}
                        >
                          <span>✏️</span>
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteQuestion(viewingAsset._id, index)}
                          style={{
                            padding: '6px 12px',
                            background: '#fef2f2',
                            color: '#dc2626',
                            border: '1px solid #fecaca',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#dc2626';
                            e.currentTarget.style.color = '#ffffff';
                            e.currentTarget.style.borderColor = '#dc2626';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#fef2f2';
                            e.currentTarget.style.color = '#dc2626';
                            e.currentTarget.style.borderColor = '#fecaca';
                          }}
                        >
                          <span>🗑️</span>
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginTop: '20px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setViewingAsset(null);
                  setEditingTranscriptTitle(false);
                  setTranscriptTitleValue('');
                }}
                className="btn btn-secondary"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setEditingAsset(viewingAsset);
                  // Reset transcript title editing
                  setEditingTranscriptTitle(false);
                  setTranscriptTitleValue('');
                  // Extract chapter ID (handle both object and string)
                  const chapterId = viewingAsset.chapter?._id || viewingAsset.chapter || null;
                  setAssetForm({
                    title: viewingAsset.title || '',
                    description: viewingAsset.description || '',
                    assetType: viewingAsset.assetType || 'note',
                    chapter: chapterId,
                    content: viewingAsset.content || '',
                    externalUrl: viewingAsset.externalUrl || '',
                    fileUrl: viewingAsset.fileUrl || '',
                    fileType: viewingAsset.fileType || '',
                    fileName: viewingAsset.fileName || '',
                    fileSize: viewingAsset.fileSize || 0,
                    filePublicId: viewingAsset.filePublicId || '',
                    order: viewingAsset.order || 0,
                    isRequired: viewingAsset.isRequired || false,
                    estimatedDuration: viewingAsset.estimatedDuration || null,
                    contentReference: viewingAsset.contentReference || { refModel: null, refId: null }
                  });
                  // Set video source type based on existing asset
                  if (viewingAsset.assetType === 'video') {
                    setVideoSourceType(viewingAsset.externalUrl ? 'url' : 'upload');
                  }
                  // Find and set the selected chapter for display
                  if (chapterId) {
                    setSelectedChapter(chapters.find(ch => ch._id === chapterId) || null);
                  } else {
                    setSelectedChapter(null);
                  }
                  setViewingAsset(null);
                  setShowAssetModal(true);
                }}
                className="btn btn-primary"
              >
                Edit
              </button>
            </div>
            </>
          )}
        </Box>
      </Modal>

      {/* Edit Study Question Modal */}
      {showQuestionModal && editingQuestion && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{ 
              padding: '24px', 
              borderBottom: '2px solid #e2e8f0',
              position: 'sticky',
              top: 0,
              background: '#ffffff',
              zIndex: 1
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#1a202c' }}>
                    ✏️ Edit Study Question
                  </h2>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                    Update the question and answer below
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowQuestionModal(false);
                    setEditingQuestion(null);
                  }}
                  style={{ 
                    padding: '8px 12px', 
                    background: '#e2e8f0', 
                    border: 'none', 
                    borderRadius: '6px', 
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  ✕
                </button>
              </div>
            </div>

            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: '600',
                  color: '#334155'
                }}>
                  Question
                </label>
                <input
                  type="text"
                  value={editingQuestion.question}
                  onChange={(e) => setEditingQuestion({ 
                    ...editingQuestion, 
                    question: e.target.value 
                  })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter the question"
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontSize: '14px', 
                  fontWeight: '600',
                  color: '#334155'
                }}>
                  Answer
                </label>
                <textarea
                  value={editingQuestion.answer}
                  onChange={(e) => setEditingQuestion({ 
                    ...editingQuestion, 
                    answer: e.target.value 
                  })}
                  rows={8}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    resize: 'vertical'
                  }}
                  placeholder="Enter the answer"
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowQuestionModal(false);
                    setEditingQuestion(null);
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#f1f5f9',
                    color: '#475569',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveQuestion}
                  disabled={!editingQuestion.question.trim() || !editingQuestion.answer.trim()}
                  style={{
                    padding: '10px 20px',
                    background: editingQuestion.question.trim() && editingQuestion.answer.trim() 
                      ? '#667eea' 
                      : '#cbd5e1',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: editingQuestion.question.trim() && editingQuestion.answer.trim() 
                      ? 'pointer' 
                      : 'not-allowed',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quiz Generation Modal */}
      {showQuizGenerationModal && viewingAsset && (
        <Modal
          open={showQuizGenerationModal}
          onClose={() => setShowQuizGenerationModal(false)}
          aria-labelledby="quiz-generation-modal-title"
          disableScrollLock={true}
          BackdropProps={{
            timeout: 0
          }}
          sx={{
            zIndex: 1400
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '5%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: { xs: '95%', sm: '90%', md: '500px' },
              maxHeight: '90vh',
              bgcolor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              p: 3,
              overflowY: 'auto',
              outline: 'none'
            }}
          >
            <h3 id="quiz-generation-modal-title" style={{ margin: '0 0 20px 0', fontSize: '20px', color: '#1a202c' }}>
              🎯 Generate Quiz for "{viewingAsset.title}"
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                Number of Questions (1-20)
              </label>
              <input
                type="number"
                min="1"
                max="20"
                value={quizGenerationSettings.numQuestions}
                onChange={(e) => setQuizGenerationSettings(prev => ({
                  ...prev,
                  numQuestions: parseInt(e.target.value) || 1
                }))}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                Question Types
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={quizGenerationSettings.questionTypes.includes('MCQ')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setQuizGenerationSettings(prev => ({
                          ...prev,
                          questionTypes: [...prev.questionTypes, 'MCQ']
                        }));
                      } else {
                        setQuizGenerationSettings(prev => ({
                          ...prev,
                          questionTypes: prev.questionTypes.filter(t => t !== 'MCQ')
                        }));
                      }
                    }}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Multiple Choice Questions</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={quizGenerationSettings.questionTypes.includes('TrueFalse')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setQuizGenerationSettings(prev => ({
                          ...prev,
                          questionTypes: [...prev.questionTypes, 'TrueFalse']
                        }));
                      } else {
                        setQuizGenerationSettings(prev => ({
                          ...prev,
                          questionTypes: prev.questionTypes.filter(t => t !== 'TrueFalse')
                        }));
                      }
                    }}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: '14px' }}>True/False Questions</span>
                </label>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                Difficulty Levels
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {['Easy', 'Medium', 'Expert'].map(diff => (
                  <label key={diff} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={quizGenerationSettings.difficulties.includes(diff)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setQuizGenerationSettings(prev => ({
                            ...prev,
                            difficulties: [...prev.difficulties, diff]
                          }));
                        } else {
                          setQuizGenerationSettings(prev => ({
                            ...prev,
                            difficulties: prev.difficulties.filter(d => d !== diff)
                          }));
                        }
                      }}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span style={{ fontSize: '14px' }}>{diff}</span>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={quizGenerationSettings.showAnswerAfterEach}
                  onChange={(e) => setQuizGenerationSettings(prev => ({
                    ...prev,
                    showAnswerAfterEach: e.target.checked
                  }))}
                  style={{ width: '18px', height: '18px' }}
                />
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                  Show answers after each question
                </span>
              </label>
              <p style={{ margin: '4px 0 0 26px', fontSize: '12px', color: '#718096' }}>
                {quizGenerationSettings.showAnswerAfterEach 
                  ? 'Students will see correct answers immediately after answering each question'
                  : 'Students will see all correct answers only at the end of the quiz'}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowQuizGenerationModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#f1f5f9',
                  color: '#475569',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateQuiz}
                disabled={generatingQuiz}
                style={{
                  padding: '10px 20px',
                  background: generatingQuiz ? '#cbd5e1' : '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: generatingQuiz ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {generatingQuiz ? (
                  <>
                    <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
                    Generating...
                  </>
                ) : (
                  <>
                    <span>🎯</span>
                    Generate Quiz
                  </>
                )}
              </button>
            </div>
          </Box>
        </Modal>
      )}

      {/* Assignment Generation Modal */}
      {showAssignmentGenerationModal && viewingAsset && (
        <Modal
          open={showAssignmentGenerationModal}
          onClose={() => {
            if (generatedAssignments.length === 0) {
              setShowAssignmentGenerationModal(false);
            }
          }}
          aria-labelledby="assignment-generation-modal-title"
          disableScrollLock={true}
          BackdropProps={{
            timeout: 0
          }}
          sx={{
            zIndex: 9999
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '5%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: { xs: '95%', sm: '90%', md: '800px' },
              maxHeight: '90vh',
              bgcolor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              p: 3,
              overflowY: 'auto',
              outline: 'none'
            }}
          >
            <h3 id="assignment-generation-modal-title" style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>
              Generate Assignments from Session Content
            </h3>
            <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#64748b' }}>
              Generate practice assignments based on the session content. Select the number and types, then review and choose which ones to save.
            </p>

            {/* Saved Assignments Section */}
            {savedAssignmentsForAsset.length > 0 && (
              <div style={{ 
                marginBottom: '24px', 
                padding: '16px', 
                background: '#f0fdf4', 
                border: '2px solid #86efac',
                borderRadius: '8px' 
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#166534' }}>
                    💾 Saved Assignments ({savedAssignmentsForAsset.length})
                  </h4>
                  <p style={{ margin: 0, fontSize: '11px', color: '#16a34a', fontStyle: 'italic' }}>
                    Click to generate problem statement
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                  {savedAssignmentsForAsset.map((assignment, idx) => (
                    <div
                      key={assignment._id || idx}
                      onClick={() => handleOpenProblemStatementModal(assignment)}
                      style={{
                        padding: '12px',
                        background: '#ffffff',
                        border: '1px solid #bbf7d0',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        ':hover': {
                          background: '#f0fdf4',
                          borderColor: '#86efac',
                          transform: 'translateX(4px)'
                        }
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = '#f0fdf4';
                        e.currentTarget.style.borderColor = '#86efac';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = '#ffffff';
                        e.currentTarget.style.borderColor = '#bbf7d0';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                          <h5 style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>
                            {assignment.title}
                          </h5>
                          <span style={{
                            fontSize: '9px',
                            fontWeight: '600',
                            color: '#ffffff',
                            background: '#10b981',
                            padding: '2px 6px',
                            borderRadius: '3px'
                          }}>
                            {assignment.type?.replace('_', ' ').toUpperCase()}
                          </span>
                          <span style={{
                            fontSize: '9px',
                            fontWeight: '600',
                            color: '#16a34a',
                            background: '#dcfce7',
                            padding: '2px 6px',
                            borderRadius: '3px'
                          }}>
                            {assignment.complexity}
                          </span>
                          <span style={{ fontSize: '10px', color: '#6b7280' }}>
                            {assignment.language?.toUpperCase()}
                          </span>
                        </div>
                        <p style={{ margin: 0, fontSize: '11px', color: '#475569', lineHeight: '1.4' }}>
                          {assignment.description?.substring(0, 100)}{assignment.description?.length > 100 ? '...' : ''}
                        </p>
                      </div>
                      <div style={{ flexShrink: 0, fontSize: '20px', color: '#10b981' }}>
                        📄
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {loadingSavedAssignments && (
              <div style={{ 
                marginBottom: '24px', 
                padding: '20px', 
                textAlign: 'center',
                color: '#64748b',
                fontSize: '14px'
              }}>
                Loading saved assignments...
              </div>
            )}

            {/* Settings Section */}
            {generatedAssignments.length === 0 && (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                    Programming Language
                  </label>
                  <select
                    value={assignmentGenerationSettings.language}
                    onChange={(e) => setAssignmentGenerationSettings(prev => ({
                      ...prev,
                      language: e.target.value
                    }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '14px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      background: '#ffffff',
                      color: '#1e293b',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="javascript">JavaScript</option>
                    <option value="python">Python</option>
                    <option value="java">Java</option>
                    <option value="sql">SQL</option>
                    <option value="csharp">C#</option>
                    <option value="typescript">TypeScript</option>
                  </select>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                      Assignment Types & Counts
                    </label>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#3b82f6' }}>
                      Total: {Object.values(assignmentGenerationSettings.typeCounts).reduce((sum, count) => sum + count, 0)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[
                      { value: 'concept_reinforcement', label: '📚 Concept Reinforcement', desc: 'Basic exercises to practice and reinforce key concepts' },
                      { value: 'debugging', label: '🐛 Debugging Challenge', desc: 'Find and fix bugs in code related to session topics' },
                      { value: 'small_coding', label: '💻 Small Coding Task', desc: 'Write specific functions or solve focused problems' },
                      { value: 'feature_implementation', label: '⚙️ Feature Implementation', desc: 'Build new features or extend existing code' },
                      { value: 'output_prediction', label: '🔮 Output Prediction', desc: 'Analyze code snippets and predict their output' },
                      { value: 'mini_project', label: '🚀 Mini Project', desc: 'Comprehensive project integrating multiple concepts' }
                    ].map(type => (
                      <div key={type.value} style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        borderRadius: '6px',
                        background: assignmentGenerationSettings.typeCounts[type.value] > 0 ? '#eff6ff' : '#f8fafc',
                        border: `1px solid ${assignmentGenerationSettings.typeCounts[type.value] > 0 ? '#3b82f6' : '#e2e8f0'}`,
                        transition: 'all 0.2s'
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{type.label}</div>
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{type.desc}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                          <button
                            onClick={() => {
                              setAssignmentGenerationSettings(prev => ({
                                ...prev,
                                typeCounts: {
                                  ...prev.typeCounts,
                                  [type.value]: Math.max(0, prev.typeCounts[type.value] - 1)
                                }
                              }));
                            }}
                            disabled={assignmentGenerationSettings.typeCounts[type.value] === 0}
                            style={{
                              width: '32px',
                              height: '32px',
                              padding: '0',
                              fontSize: '18px',
                              fontWeight: '600',
                              cursor: assignmentGenerationSettings.typeCounts[type.value] === 0 ? 'not-allowed' : 'pointer',
                              border: '1px solid #cbd5e1',
                              borderRadius: '6px',
                              background: assignmentGenerationSettings.typeCounts[type.value] === 0 ? '#f1f5f9' : '#ffffff',
                              color: assignmentGenerationSettings.typeCounts[type.value] === 0 ? '#cbd5e1' : '#475569',
                              transition: 'all 0.2s'
                            }}
                          >
                            −
                          </button>
                          <div style={{
                            width: '40px',
                            textAlign: 'center',
                            fontSize: '16px',
                            fontWeight: '700',
                            color: assignmentGenerationSettings.typeCounts[type.value] > 0 ? '#3b82f6' : '#94a3b8'
                          }}>
                            {assignmentGenerationSettings.typeCounts[type.value]}
                          </div>
                          <button
                            onClick={() => {
                              const currentTotal = Object.values(assignmentGenerationSettings.typeCounts).reduce((sum, count) => sum + count, 0);
                              if (currentTotal < 20) {
                                setAssignmentGenerationSettings(prev => ({
                                  ...prev,
                                  typeCounts: {
                                    ...prev.typeCounts,
                                    [type.value]: Math.min(10, prev.typeCounts[type.value] + 1)
                                  }
                                }));
                              }
                            }}
                            disabled={assignmentGenerationSettings.typeCounts[type.value] >= 10 || Object.values(assignmentGenerationSettings.typeCounts).reduce((sum, count) => sum + count, 0) >= 20}
                            style={{
                              width: '32px',
                              height: '32px',
                              padding: '0',
                              fontSize: '18px',
                              fontWeight: '600',
                              cursor: (assignmentGenerationSettings.typeCounts[type.value] >= 10 || Object.values(assignmentGenerationSettings.typeCounts).reduce((sum, count) => sum + count, 0) >= 20) ? 'not-allowed' : 'pointer',
                              border: '1px solid #cbd5e1',
                              borderRadius: '6px',
                              background: (assignmentGenerationSettings.typeCounts[type.value] >= 10 || Object.values(assignmentGenerationSettings.typeCounts).reduce((sum, count) => sum + count, 0) >= 20) ? '#f1f5f9' : '#ffffff',
                              color: (assignmentGenerationSettings.typeCounts[type.value] >= 10 || Object.values(assignmentGenerationSettings.typeCounts).reduce((sum, count) => sum + count, 0) >= 20) ? '#cbd5e1' : '#475569',
                              transition: 'all 0.2s'
                            }}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
                    Max 10 per type • Max 20 total
                  </div>
                </div>
              </>
            )}

            {/* Generated Assignments List */}
            {generatedAssignments.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '2px solid #e5e7eb' }}>
                  <div>
                    <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                      ✅ Generated Assignments
                    </h4>
                    <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                      {selectedAssignments.length} of {generatedAssignments.length} selected
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => {
                        if (selectedAssignments.length === generatedAssignments.length) {
                          setSelectedAssignments([]);
                        } else {
                          setSelectedAssignments(generatedAssignments.map((_, idx) => idx));
                        }
                      }}
                      style={{
                        padding: '6px 12px',
                        background: '#f1f5f9',
                        color: '#475569',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}
                    >
                      {selectedAssignments.length === generatedAssignments.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '50vh', overflowY: 'auto', paddingRight: '8px' }}>
                  {generatedAssignments.map((assignment, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '16px',
                        background: selectedAssignments.includes(idx) ? '#ffffff' : '#f9fafb',
                        border: `2px solid ${selectedAssignments.includes(idx) ? '#8b5cf6' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        display: 'flex',
                        gap: '12px',
                        transition: 'all 0.2s'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedAssignments.includes(idx)}
                        onChange={() => handleToggleAssignmentSelection(idx)}
                        style={{ width: '20px', height: '20px', flexShrink: 0, cursor: 'pointer', marginTop: '2px' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                          <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                            {assignment.title}
                          </h4>
                          <span style={{
                            fontSize: '10px',
                            fontWeight: '600',
                            color: '#ffffff',
                            background: assignment.type === 'mini_project' ? '#8b5cf6' :
                              assignment.type === 'feature_implementation' ? '#3b82f6' :
                              assignment.type === 'debugging' ? '#ef4444' :
                              assignment.type === 'small_coding' ? '#10b981' :
                              assignment.type === 'output_prediction' ? '#f59e0b' : '#6366f1',
                            padding: '3px 8px',
                            borderRadius: '3px'
                          }}>
                            {assignment.type.replace('_', ' ').toUpperCase()}
                          </span>
                          <span style={{
                            fontSize: '10px',
                            fontWeight: '600',
                            color: assignment.complexity === 'Expert' ? '#ef4444' : assignment.complexity === 'Medium' ? '#f59e0b' : '#10b981',
                            background: assignment.complexity === 'Expert' ? '#fee2e2' : assignment.complexity === 'Medium' ? '#fef3c7' : '#d1fae5',
                            padding: '3px 8px',
                            borderRadius: '3px'
                          }}>
                            {assignment.complexity}
                          </span>
                          <span style={{ fontSize: '11px', color: '#6b7280' }}>
                            ⏱️ {assignment.estimatedTime}
                          </span>
                        </div>

                        <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#475569', lineHeight: '1.5' }}>
                          {assignment.description}
                        </p>

                        <div style={{ marginBottom: '10px' }}>
                          <strong style={{ fontSize: '11px', color: '#374151', display: 'block', marginBottom: '4px' }}>
                            🎯 Objectives:
                          </strong>
                          <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '11px', color: '#6b7280' }}>
                            {assignment.objectives.slice(0, 3).map((obj, objIdx) => (
                              <li key={objIdx} style={{ marginBottom: '2px' }}>{obj}</li>
                            ))}
                            {assignment.objectives.length > 3 && (
                              <li style={{ color: '#8b5cf6', fontStyle: 'italic' }}>
                                +{assignment.objectives.length - 3} more...
                              </li>
                            )}
                          </ul>
                        </div>

                        {assignment.relatedTopics && assignment.relatedTopics.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {assignment.relatedTopics.slice(0, 5).map((topic, topIdx) => (
                              <span
                                key={topIdx}
                                style={{
                                  fontSize: '10px',
                                  color: '#6b7280',
                                  background: '#f3f4f6',
                                  padding: '2px 6px',
                                  borderRadius: '3px'
                                }}
                              >
                                #{topic}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <button
                onClick={() => {
                  setShowAssignmentGenerationModal(false);
                  setGeneratedAssignments([]);
                  setSelectedAssignments([]);
                  setSavedAssignmentsForAsset([]);
                }}
                style={{
                  padding: '10px 20px',
                  background: '#f1f5f9',
                  color: '#475569',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {generatedAssignments.length > 0 ? 'Close' : 'Cancel'}
              </button>
              
              {generatedAssignments.length === 0 ? (
                <button
                  onClick={handleGenerateAssignments}
                  disabled={generatingAssignments || Object.values(assignmentGenerationSettings.typeCounts).reduce((sum, count) => sum + count, 0) === 0}
                  style={{
                    padding: '10px 20px',
                    background: (generatingAssignments || Object.values(assignmentGenerationSettings.typeCounts).reduce((sum, count) => sum + count, 0) === 0) ? '#cbd5e1' : '#3b82f6',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: (generatingAssignments || Object.values(assignmentGenerationSettings.typeCounts).reduce((sum, count) => sum + count, 0) === 0) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {generatingAssignments ? (
                    <>
                      <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
                      Generating...
                    </>
                  ) : (
                    <>
                      <span>📝</span>
                      Generate Assignments
                    </>
                  )}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setGeneratedAssignments([]);
                      setSelectedAssignments([]);
                    }}
                    style={{
                      padding: '10px 20px',
                      background: '#ffffff',
                      color: '#3b82f6',
                      border: '1px solid #3b82f6',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    Regenerate
                  </button>
                  <button
                    onClick={async () => {
                      await handleSaveSelectedAssignments();
                    }}
                    disabled={selectedAssignments.length === 0}
                    style={{
                      padding: '10px 20px',
                      background: selectedAssignments.length === 0 ? '#cbd5e1' : '#10b981',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: selectedAssignments.length === 0 ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span>💾</span>
                    Save Selected ({selectedAssignments.length})
                  </button>
                </>
              )}
            </div>
          </Box>
        </Modal>
      )}

      {/* Problem Statement Generation Modal */}
      {showProblemStatementModal && selectedAssignmentForStatement && (
        <Modal
          open={showProblemStatementModal}
          onClose={() => setShowProblemStatementModal(false)}
          aria-labelledby="problem-statement-modal-title"
          disableScrollLock={true}
          BackdropProps={{ timeout: 0 }}
          sx={{ zIndex: 10000 }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '5%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: { xs: '95%', sm: '90%', md: '900px' },
              maxHeight: '90vh',
              bgcolor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              p: 3,
              overflowY: 'auto',
              outline: 'none'
            }}
          >
            <h3 id="problem-statement-modal-title" style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>
              📄 Generate Problem Statement
            </h3>
            <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#64748b' }}>
              Generate a problem statement for: <strong>{selectedAssignmentForStatement.title}</strong>
            </p>
            <div style={{ marginBottom: '20px', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', fontWeight: '600', color: '#ffffff', background: '#10b981', padding: '3px 8px', borderRadius: '4px' }}>
                {selectedAssignmentForStatement.type?.replace('_', ' ').toUpperCase()}
              </span>
              <span style={{ fontSize: '11px', fontWeight: '600', color: '#16a34a', background: '#dcfce7', padding: '3px 8px', borderRadius: '4px' }}>
                {selectedAssignmentForStatement.complexity}
              </span>
              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                {selectedAssignmentForStatement.language?.toUpperCase()}
              </span>
            </div>

            {/* Additional Input Textarea */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                Additional Instructions (Optional)
              </label>
              <textarea
                value={problemStatementInput}
                onChange={(e) => setProblemStatementInput(e.target.value)}
                placeholder="Add any specific requirements, constraints, or context you want the AI to consider when generating the problem statement..."
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '12px',
                  fontSize: '14px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  color: '#1e293b'
                }}
              />
              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                Example: "Focus on error handling", "Include edge cases for empty arrays", "Emphasize time complexity"
              </p>
            </div>

            {/* Generation Buttons */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                Select Statement Length
              </label>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => handleGenerateProblemStatement('short')}
                  disabled={generatingStatement}
                  style={{
                    flex: 1,
                    minWidth: '150px',
                    padding: '16px 20px',
                    background: generatingStatement && statementLength === 'short' ? '#cbd5e1' : '#ffffff',
                    color: '#1e293b',
                    border: '2px solid #3b82f6',
                    borderRadius: '8px',
                    cursor: generatingStatement ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>📋</span>
                  <span>Short</span>
                  <span style={{ fontSize: '11px', fontWeight: 'normal', color: '#64748b' }}>150-250 words</span>
                </button>
                <button
                  onClick={() => handleGenerateProblemStatement('medium')}
                  disabled={generatingStatement}
                  style={{
                    flex: 1,
                    minWidth: '150px',
                    padding: '16px 20px',
                    background: generatingStatement && statementLength === 'medium' ? '#cbd5e1' : '#ffffff',
                    color: '#1e293b',
                    border: '2px solid #8b5cf6',
                    borderRadius: '8px',
                    cursor: generatingStatement ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>📄</span>
                  <span>Medium</span>
                  <span style={{ fontSize: '11px', fontWeight: 'normal', color: '#64748b' }}>300-500 words</span>
                </button>
                <button
                  onClick={() => handleGenerateProblemStatement('full')}
                  disabled={generatingStatement}
                  style={{
                    flex: 1,
                    minWidth: '150px',
                    padding: '16px 20px',
                    background: generatingStatement && statementLength === 'full' ? '#cbd5e1' : '#ffffff',
                    color: '#1e293b',
                    border: '2px solid #10b981',
                    borderRadius: '8px',
                    cursor: generatingStatement ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>📚</span>
                  <span>Full</span>
                  <span style={{ fontSize: '11px', fontWeight: 'normal', color: '#64748b' }}>600-1000 words</span>
                </button>
              </div>
            </div>

            {/* Loading State */}
            {generatingStatement && (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                background: '#f8fafc',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <div style={{ fontSize: '30px', marginBottom: '12px', animation: 'spin 2s linear infinite' }}>⟳</div>
                <p style={{ margin: 0, fontSize: '14px', color: '#475569', fontWeight: '600' }}>
                  Generating {statementLength} problem statement...
                </p>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                  This may take a few moments
                </p>
              </div>
            )}

            {/* Generated Statement Display */}
            {generatedProblemStatement && !generatingStatement && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>
                    ✅ Generated Statement ({statementLength})
                  </h4>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handleCopyProblemStatement}
                      style={{
                        padding: '6px 12px',
                        background: '#3b82f6',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      📋 Copy
                    </button>
                    <button
                      onClick={() => handleSaveProblemStatement(false)}
                      style={{
                        padding: '6px 12px',
                        background: '#10b981',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      💾 Save
                    </button>
                    <button
                      onClick={() => handleSaveProblemStatement(true)}
                      style={{
                        padding: '6px 12px',
                        background: '#8b5cf6',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      ⭐ Save & Set Active
                    </button>
                  </div>
                </div>
                <div style={{
                  padding: '20px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  fontSize: '14px',
                  lineHeight: '1.7',
                  color: '#1e293b',
                  whiteSpace: 'pre-wrap'
                }}>
                  {generatedProblemStatement}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <button
                onClick={() => {
                  setShowProblemStatementModal(false);
                  setGeneratedProblemStatement('');
                  setProblemStatementInput('');
                  setStatementLength('');
                }}
                style={{
                  padding: '10px 20px',
                  background: '#f1f5f9',
                  color: '#475569',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Close
              </button>
            </div>
          </Box>
        </Modal>
      )}

      {/* Study Questions Generation Modal */}
      {showStudyQuestionsModal && viewingAsset && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200
          }}
          onClick={() => setShowStudyQuestionsModal(false)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              padding: '24px',
              width: '90%',
              maxWidth: '500px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', color: '#1a202c' }}>
              💡 Generate Study Questions for "{viewingAsset.title}"
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                Number of Questions (1-15)
              </label>
              <input
                type="number"
                min="1"
                max="15"
                value={studyQuestionsSettings.numQuestions}
                onChange={(e) => setStudyQuestionsSettings(prev => ({
                  ...prev,
                  numQuestions: parseInt(e.target.value) || 1
                }))}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#718096' }}>
                Study questions help students understand key concepts while reading the content.
                Questions are generated with detailed answers.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowStudyQuestionsModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#f1f5f9',
                  color: '#475569',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmGenerateStudyQuestions}
                disabled={generatingQuestions}
                style={{
                  padding: '10px 20px',
                  background: generatingQuestions ? '#cbd5e1' : '#667eea',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: generatingQuestions ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {generatingQuestions ? (
                  <>
                    <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
                    Generating...
                  </>
                ) : (
                  <>
                    <span>✨</span>
                    Generate Questions
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>

      {/* Teaching Analysis Modal */}
      {teachingAnalysisAsset && (
        <TeachingAnalysisModal
          show={showTeachingAnalysis}
          onHide={() => {
            setShowTeachingAnalysis(false);
            setTeachingAnalysisAsset(null);
          }}
          assetId={teachingAnalysisAsset._id}
          assetTitle={teachingAnalysisAsset.title}
        />
      )}

      {/* Lecture Assignment Details Modal */}
      {viewingLectureAssignment && (
        <Modal
          open={!!viewingLectureAssignment}
          onClose={() => setViewingLectureAssignment(null)}
          aria-labelledby="lecture-assignment-details-modal"
          disableScrollLock={true}
          BackdropProps={{ timeout: 0 }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '5%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: { xs: '95%', sm: '90%', md: '800px' },
              maxHeight: '90vh',
              bgcolor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              p: 3,
              overflowY: 'auto',
              outline: 'none'
            }}
          >
            {/* Close Button */}
            <IconButton
              onClick={() => setViewingLectureAssignment(null)}
              sx={{
                position: 'absolute',
                right: 16,
                top: 16,
                color: '#64748b',
                '&:hover': { background: '#f1f5f9' }
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Header */}
            <div style={{ marginBottom: '24px', paddingRight: '40px' }}>
              <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1e293b', margin: '0 0 12px 0' }}>
                {viewingLectureAssignment.title}
              </h2>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  background: viewingLectureAssignment.type === 'mini_project' ? '#fef3c7' : 
                             viewingLectureAssignment.type === 'debugging' ? '#fee2e2' :
                             viewingLectureAssignment.type === 'feature_implementation' ? '#ddd6fe' :
                             viewingLectureAssignment.type === 'small_coding' ? '#dbeafe' :
                             viewingLectureAssignment.type === 'output_prediction' ? '#cffafe' :
                             '#d1fae5',
                  color: viewingLectureAssignment.type === 'mini_project' ? '#92400e' : 
                         viewingLectureAssignment.type === 'debugging' ? '#991b1b' :
                         viewingLectureAssignment.type === 'feature_implementation' ? '#5b21b6' :
                         viewingLectureAssignment.type === 'small_coding' ? '#1e40af' :
                         viewingLectureAssignment.type === 'output_prediction' ? '#155e75' :
                         '#065f46'
                }}>
                  {viewingLectureAssignment.type.replace(/_/g, ' ').toUpperCase()}
                </span>
                <span style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  background: viewingLectureAssignment.complexity === 'Easy' ? '#d1fae5' : 
                             viewingLectureAssignment.complexity === 'Medium' ? '#fef3c7' : '#fee2e2',
                  color: viewingLectureAssignment.complexity === 'Easy' ? '#065f46' : 
                         viewingLectureAssignment.complexity === 'Medium' ? '#92400e' : '#991b1b'
                }}>
                  {viewingLectureAssignment.complexity}
                </span>
                <span style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  background: '#f1f5f9',
                  color: '#475569'
                }}>
                  {viewingLectureAssignment.language.toUpperCase()}
                </span>
                <span style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  background: '#f1f5f9',
                  color: '#475569'
                }}>
                  ⏱️ {viewingLectureAssignment.estimatedTime}
                </span>
              </div>
            </div>

            {/* Description */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', margin: '0 0 8px 0' }}>
                📄 Brief Overview
              </h3>
              <p style={{ fontSize: '14px', color: '#1e293b', lineHeight: '1.6', margin: 0 }}>
                {viewingLectureAssignment.description}
              </p>
            </div>

            {/* Problem Statement */}
            {viewingLectureAssignment.problemStatement && viewingLectureAssignment.problemStatement !== viewingLectureAssignment.description && (
              <div style={{ marginBottom: '24px', padding: '16px', background: '#fef9f3', borderLeft: '4px solid #f59e0b', borderRadius: '8px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#92400e', margin: '0 0 8px 0' }}>
                  📋 Full Problem Statement
                </h3>
                <p style={{ fontSize: '14px', color: '#1e293b', lineHeight: '1.7', margin: 0, whiteSpace: 'pre-wrap' }}>
                  {viewingLectureAssignment.problemStatement}
                </p>
              </div>
            )}

            {/* Detailed Instructions */}
            {viewingLectureAssignment.detailedInstructions && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', margin: '0 0 8px 0' }}>
                  📝 Step-by-Step Instructions
                </h3>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#1e293b', 
                  lineHeight: '1.7', 
                  whiteSpace: 'pre-wrap',
                  padding: '12px',
                  background: '#f8fafc',
                  borderRadius: '6px'
                }}>
                  {viewingLectureAssignment.detailedInstructions}
                </div>
              </div>
            )}

            {/* Starter Code */}
            {viewingLectureAssignment.starterCode && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', margin: '0 0 8px 0' }}>
                  🚀 Starter Code
                </h3>
                <pre style={{ 
                  margin: 0, 
                  padding: '16px', 
                  background: '#1e293b', 
                  color: '#e2e8f0',
                  borderRadius: '8px',
                  overflow: 'auto',
                  fontSize: '13px',
                  lineHeight: '1.6'
                }}>
                  <code>{viewingLectureAssignment.starterCode}</code>
                </pre>
              </div>
            )}

            {/* Test Cases */}
            {viewingLectureAssignment.testCases && viewingLectureAssignment.testCases.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', margin: '0 0 12px 0' }}>
                  🧪 Test Cases
                </h3>
                {viewingLectureAssignment.testCases.map((testCase, idx) => (
                  <div key={idx} style={{
                    marginBottom: '12px',
                    padding: '12px',
                    background: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: '6px'
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#166534', marginBottom: '6px' }}>
                      Test Case {idx + 1}
                    </div>
                    {testCase.input && (
                      <div style={{ marginBottom: '6px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#15803d' }}>Input: </span>
                        <span style={{ fontSize: '12px', color: '#166534' }}>{testCase.input}</span>
                      </div>
                    )}
                    {testCase.expectedOutput && (
                      <div style={{ marginBottom: '6px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#15803d' }}>Expected Output: </span>
                        <span style={{ fontSize: '12px', color: '#166534' }}>{testCase.expectedOutput}</span>
                      </div>
                    )}
                    {testCase.explanation && (
                      <div style={{ fontSize: '12px', color: '#16a34a', fontStyle: 'italic' }}>
                        {testCase.explanation}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Solution Code */}
            {viewingLectureAssignment.solutionCode && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', margin: '0 0 8px 0' }}>
                  ✅ Solution Code (Reference)
                </h3>
                <pre style={{ 
                  margin: 0, 
                  padding: '16px', 
                  background: '#064e3b', 
                  color: '#d1fae5',
                  borderRadius: '8px',
                  overflow: 'auto',
                  fontSize: '13px',
                  lineHeight: '1.6'
                }}>
                  <code>{viewingLectureAssignment.solutionCode}</code>
                </pre>
              </div>
            )}

            {/* Learning Objectives */}
            {viewingLectureAssignment.objectives && viewingLectureAssignment.objectives.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', margin: '0 0 8px 0' }}>
                  🎯 Learning Objectives
                </h3>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {viewingLectureAssignment.objectives.map((obj, idx) => (
                    <li key={idx} style={{ fontSize: '14px', color: '#1e293b', lineHeight: '1.6', marginBottom: '4px' }}>
                      {obj}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Acceptance Criteria */}
            {viewingLectureAssignment.acceptanceCriteria && viewingLectureAssignment.acceptanceCriteria.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', margin: '0 0 8px 0' }}>
                  ✅ Acceptance Criteria
                </h3>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {viewingLectureAssignment.acceptanceCriteria.map((criteria, idx) => (
                    <li key={idx} style={{ fontSize: '14px', color: '#1e293b', lineHeight: '1.6', marginBottom: '4px' }}>
                      {criteria}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Hints */}
            {viewingLectureAssignment.hints && viewingLectureAssignment.hints.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', margin: '0 0 8px 0' }}>
                  💡 Hints
                </h3>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {viewingLectureAssignment.hints.map((hint, idx) => (
                    <li key={idx} style={{ fontSize: '14px', color: '#1e293b', lineHeight: '1.6', marginBottom: '4px' }}>
                      {hint}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Related Topics */}
            {viewingLectureAssignment.relatedTopics && viewingLectureAssignment.relatedTopics.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#475569', margin: '0 0 8px 0' }}>
                  🏷️ Related Topics
                </h3>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {viewingLectureAssignment.relatedTopics.map((topic, idx) => (
                    <span
                      key={idx}
                      style={{
                        fontSize: '12px',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        background: '#eff6ff',
                        color: '#1e40af',
                        border: '1px solid #bfdbfe'
                      }}
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Notes */}
            {viewingLectureAssignment.additionalNotes && (
              <div style={{ marginBottom: '24px', padding: '16px', background: '#fef3c7', borderLeft: '4px solid #eab308', borderRadius: '8px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#854d0e', margin: '0 0 8px 0' }}>
                  💭 Additional Notes
                </h3>
                <p style={{ fontSize: '13px', color: '#713f12', lineHeight: '1.6', margin: 0, whiteSpace: 'pre-wrap' }}>
                  {viewingLectureAssignment.additionalNotes}
                </p>
              </div>
            )}

            {/* Source Asset */}
            {viewingLectureAssignment.sourceAsset && (
              <div style={{ 
                marginTop: '24px',
                paddingTop: '24px',
                borderTop: '1px solid #e2e8f0'
              }}>
                <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                  Generated from: <span style={{ fontWeight: '600', color: '#64748b' }}>
                    {viewingLectureAssignment.sourceAsset.title || 'Session Content'}
                  </span>
                </div>
                {viewingLectureAssignment.generationMetadata && (
                  <div style={{ fontSize: '11px', color: '#cbd5e1', marginTop: '4px' }}>
                    AI Model: {viewingLectureAssignment.generationMetadata.aiModel} | 
                    Generated: {new Date(viewingLectureAssignment.generationMetadata.generatedAt).toLocaleDateString()}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'flex-end',
              marginTop: '24px',
              paddingTop: '24px',
              borderTop: '1px solid #e2e8f0'
            }}>
              <button
                onClick={() => setViewingLectureAssignment(null)}
                style={{
                  padding: '10px 20px',
                  background: '#f1f5f9',
                  color: '#475569',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Close
              </button>
            </div>
          </Box>
        </Modal>
      )}
    </>
  );
}

export default CourseBuilder;
